import {
  canGroupTribes,
  createDefaultTribes,
  listContiguousTribeWindows,
  normalizeTribes,
} from '../settings/locationModel';
import {
  getRequiredAllowedRotations,
  getRequiredAssignmentCount,
  getRequiredSchedule,
  getRequiredSchedulePatterns,
  REQUIRED_SCHEDULE_MODE_PER_BLOCK,
  requiredScheduleIsSatisfied,
} from '../settings/requiredSchedule';

const ROTATION_COUNT = 6;
const ONE_EIGHT = 'ONE EIGHT';
const MASU_MADNESS = 'MASU MADNESS';
const DEFAULT_DYNAMIC_MIN_DELTA = 7;
const PREFERENCE_DEMAND_WEIGHTS = [10, 7, 5, 3];

function getPreferenceDemandWeight(rank) {
  if (!Number.isFinite(rank) || rank <= 0) return 0;
  return PREFERENCE_DEMAND_WEIGHTS[rank - 1] || 1;
}

function getCamperAssignmentKey(camper) {
  return camper?.camperId || camper?.name || '';
}

function getSkillConfig(skill, skillsConfig) {
  return (skillsConfig || {})[skill] || {};
}

function getSkillRotationOverride(skill, rotation, skillsConfig) {
  const config = getSkillConfig(skill, skillsConfig);
  const overrides = Array.isArray(config.rotationOverrides) ? config.rotationOverrides : [];
  return overrides.find((entry) => Number(entry.rotationIndex) === rotation) || null;
}

function getSkillMaxSize(skill, defaultGroupMax, skillsConfig, rotation = null) {
  if (rotation != null) {
    const override = getSkillRotationOverride(skill, rotation, skillsConfig);
    const overrideMax = override?.override?.maxGroupSize;
    if (Number.isFinite(overrideMax)) return overrideMax;
  }
  const config = getSkillConfig(skill, skillsConfig);
  return Number.isFinite(config.maxGroupSize) ? config.maxGroupSize : defaultGroupMax;
}

function getSkillMinSize(skill, defaultGroupMin, skillsConfig, rotation = null) {
  if (rotation != null) {
    const override = getSkillRotationOverride(skill, rotation, skillsConfig);
    const overrideMin = override?.override?.minGroupSize;
    if (Number.isFinite(overrideMin)) return overrideMin;
  }
  const config = getSkillConfig(skill, skillsConfig);
  return Number.isFinite(config.minGroupSize) ? config.minGroupSize : defaultGroupMin;
}

function getSkillFixedRotations(skill, skillsConfig) {
  const config = getSkillConfig(skill, skillsConfig);
  return Array.isArray(config.fixedRotations) ? config.fixedRotations : [];
}

function skillAllowsRotation(skill, rotation, skillsConfig) {
  const config = getSkillConfig(skill, skillsConfig);
  const override = getSkillRotationOverride(skill, rotation, skillsConfig);
  if (override && override.enabled === false) return false;
  const fixedRotations = Array.isArray(config.fixedRotations) ? config.fixedRotations : [];
  if (config.requiredForEligible && getRequiredSchedule(config).mode === REQUIRED_SCHEDULE_MODE_PER_BLOCK) {
    return getRequiredAllowedRotations(config).includes(rotation);
  }
  return fixedRotations.length === 0 || fixedRotations.includes(rotation);
}

function toIntSeed(seed) {
  if (typeof seed === 'number' && Number.isFinite(seed)) return (seed >>> 0) || 1;
  if (typeof seed === 'string') {
    let acc = 0;
    for (let i = 0; i < seed.length; i++) {
      acc = (Math.imul(acc, 31) + seed.charCodeAt(i)) >>> 0;
    }
    return acc || 1;
  }
  return 1;
}

export function buildDeterministicSeed(input) {
  const text = JSON.stringify({
    campers: (input.campers || []).map((c) => ({
      camperId: getCamperAssignmentKey(c),
      gender: c.gender,
      grade: c.grade,
      tribe: c.tribe,
      preferences: c.preferences,
    })),
    staffers: (input.staffers || []).map((s) => ({
      name: s.name,
      skills: s.skills,
    })),
    settings: input.settings || {},
  });
  // FNV-1a 32-bit hash
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash || 1;
}

function createRng(seed) {
  let t = toIntSeed(seed);
  return function rand() {
    t += 0x6d2b79f5;
    let x = t;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleInPlace(arr, rand) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function randomChoice(arr, rand) {
  if (!arr || arr.length === 0) return null;
  return arr[Math.floor(rand() * arr.length)];
}

function mergeStageCounters(target, source) {
  if (!source) return target;
  Object.keys(source).forEach((k) => {
    target[k] = (target[k] || 0) + (source[k] || 0);
  });
  return target;
}

function mergeStageTimings(target, source) {
  if (!source) return target;
  Object.keys(source).forEach((k) => {
    target[k] = (target[k] || 0) + (source[k] || 0);
  });
  return target;
}

function staffCanTeach(staffer, skill) {
  const skills = staffer.skills || [];
  return skills.includes(skill);
}

function isSkillAllowedForCamper(skill, camper, skillsConfig) {
  const config = (skillsConfig || {})[skill];
  if (!config) return true;
  const allowedGenders = Array.isArray(config.allowedGenders) ? config.allowedGenders : [];
  const allowsMale = allowedGenders.includes('Male');
  const allowsFemale = allowedGenders.includes('Female');
  const unrestrictedGender = allowedGenders.length === 0 || (allowsMale && allowsFemale);
  const genderOk = camper.gender === 'Unspecified'
    ? unrestrictedGender
    : unrestrictedGender || allowedGenders.includes(camper.gender);
  const tribeOk = !config.allowedTribes || config.allowedTribes.includes(camper.tribe);
  return genderOk && tribeOk;
}

function getLocationTribes(settings = {}) {
  const configured = settings.locationConfig?.tribes;
  const tribes = Array.isArray(configured) && configured.length > 0 ? configured : createDefaultTribes();
  return normalizeTribes(tribes);
}

function getSkillEligibleTribeWindows(skill, settings, skillsConfig) {
  const config = getSkillConfig(skill, skillsConfig);
  return listContiguousTribeWindows(getLocationTribes(settings), config.allowedTribes || []);
}

function getWindowKey(window) {
  return (window || []).join('|');
}

function getSkillRepeatLimit(skill, camper, skillsConfig) {
  const config = getSkillConfig(skill, skillsConfig);
  if (config.repeatable === true) return ROTATION_COUNT;
  if (!config.requiredForEligible || !isSkillAllowedForCamper(skill, camper, skillsConfig)) return 1;
  return getRequiredAssignmentCount(config);
}

function hasReachedSkillLimit(assignedSkillSet, camper, skill, skillsConfig) {
  const currentCount = assignedSkillSet?._counts?.[skill] || (assignedSkillSet?.has(skill) ? 1 : 0);
  return currentCount >= getSkillRepeatLimit(skill, camper, skillsConfig);
}

function computeDemandTables(campers, settings = {}, skillsConfig = {}) {
  const preferenceDemandBySkill = {};
  const preferenceDemandBySkillTribe = {};
  const weightedPreferenceDemandBySkill = {};
  const weightedPreferenceDemandBySkillTribe = {};
  const eligiblePreferenceDemandBySkill = {};
  const eligiblePreferenceDemandBySkillTribe = {};
  const weightedEligiblePreferenceDemandBySkill = {};
  const weightedEligiblePreferenceDemandBySkillTribe = {};
  const requiredDemandBySkill = {};
  const requiredDemandBySkillTribe = {};
  const rankDemandBySkill = {};
  campers.forEach((camper) => {
    (camper.preferences || []).forEach((skill, idx) => {
      const rank = idx + 1;
      const weight = getPreferenceDemandWeight(rank);
      preferenceDemandBySkill[skill] = (preferenceDemandBySkill[skill] || 0) + 1;
      weightedPreferenceDemandBySkill[skill] = (weightedPreferenceDemandBySkill[skill] || 0) + weight;
      if (!rankDemandBySkill[skill]) {
        rankDemandBySkill[skill] = { 1: 0, 2: 0, 3: 0, '4+': 0 };
      }
      if (rank <= 3) rankDemandBySkill[skill][rank] += 1;
      else rankDemandBySkill[skill]['4+'] += 1;
      if (!preferenceDemandBySkillTribe[skill]) {
        preferenceDemandBySkillTribe[skill] = {};
      }
      preferenceDemandBySkillTribe[skill][camper.tribe] = (preferenceDemandBySkillTribe[skill][camper.tribe] || 0) + 1;
      if (!weightedPreferenceDemandBySkillTribe[skill]) {
        weightedPreferenceDemandBySkillTribe[skill] = {};
      }
      weightedPreferenceDemandBySkillTribe[skill][camper.tribe] = (weightedPreferenceDemandBySkillTribe[skill][camper.tribe] || 0) + weight;
      if (isSkillAllowedForCamper(skill, camper, skillsConfig)) {
        eligiblePreferenceDemandBySkill[skill] = (eligiblePreferenceDemandBySkill[skill] || 0) + 1;
        weightedEligiblePreferenceDemandBySkill[skill] = (weightedEligiblePreferenceDemandBySkill[skill] || 0) + weight;
        if (!eligiblePreferenceDemandBySkillTribe[skill]) {
          eligiblePreferenceDemandBySkillTribe[skill] = {};
        }
        eligiblePreferenceDemandBySkillTribe[skill][camper.tribe] = (eligiblePreferenceDemandBySkillTribe[skill][camper.tribe] || 0) + 1;
        if (!weightedEligiblePreferenceDemandBySkillTribe[skill]) {
          weightedEligiblePreferenceDemandBySkillTribe[skill] = {};
        }
        weightedEligiblePreferenceDemandBySkillTribe[skill][camper.tribe] = (weightedEligiblePreferenceDemandBySkillTribe[skill][camper.tribe] || 0) + weight;
      }
    });
  });

  Object.keys(skillsConfig || {}).forEach((skill) => {
    const config = getSkillConfig(skill, skillsConfig);
    if (!config.requiredForEligible) return;
    const eligibleCampers = campers.filter((camper) => isSkillAllowedForCamper(skill, camper, skillsConfig));
    if (eligibleCampers.length === 0) return;
    const multiplier = getRequiredAssignmentCount(config);
    requiredDemandBySkill[skill] = (requiredDemandBySkill[skill] || 0) + (eligibleCampers.length * multiplier);
    if (!rankDemandBySkill[skill]) {
      rankDemandBySkill[skill] = { 1: 0, 2: 0, 3: 0, '4+': 0 };
    }
    if (!requiredDemandBySkillTribe[skill]) requiredDemandBySkillTribe[skill] = {};
    eligibleCampers.forEach((camper) => {
      requiredDemandBySkillTribe[skill][camper.tribe] = (requiredDemandBySkillTribe[skill][camper.tribe] || 0) + multiplier;
    });
  });

  const demandBySkill = { ...preferenceDemandBySkill };
  const demandBySkillTribe = {};
  Object.keys(preferenceDemandBySkillTribe).forEach((skill) => {
    demandBySkillTribe[skill] = { ...preferenceDemandBySkillTribe[skill] };
  });
  Object.keys(requiredDemandBySkill).forEach((skill) => {
    demandBySkill[skill] = Math.max(demandBySkill[skill] || 0, requiredDemandBySkill[skill] || 0);
    if (!demandBySkillTribe[skill]) demandBySkillTribe[skill] = {};
    Object.keys(requiredDemandBySkillTribe[skill] || {}).forEach((tribe) => {
      demandBySkillTribe[skill][tribe] = Math.max(
        demandBySkillTribe[skill][tribe] || 0,
        requiredDemandBySkillTribe[skill][tribe] || 0
      );
    });
  });

  return {
    demandBySkill,
    demandBySkillTribe,
    preferenceDemandBySkill,
    preferenceDemandBySkillTribe,
    eligiblePreferenceDemandBySkill,
    eligiblePreferenceDemandBySkillTribe,
    weightedDemandBySkill: weightedPreferenceDemandBySkill,
    weightedDemandBySkillTribe: weightedPreferenceDemandBySkillTribe,
    weightedPreferenceDemandBySkill,
    weightedPreferenceDemandBySkillTribe,
    weightedEligiblePreferenceDemandBySkill,
    weightedEligiblePreferenceDemandBySkillTribe,
    requiredDemandBySkill,
    requiredDemandBySkillTribe,
    rankDemandBySkill,
  };
}

function findOneEightTeacher(staffers, rand) {
  const candidates = staffers.filter((s) => staffCanTeach(s, ONE_EIGHT));
  if (candidates.length === 0) return null;
  const sorted = candidates.slice().sort((a, b) => a.name.localeCompare(b.name));
  return sorted[Math.floor(rand() * sorted.length)];
}

const camperPreferenceCache = new WeakMap();

function getCamperPreferenceMeta(camper) {
  let meta = camperPreferenceCache.get(camper);
  if (meta) return meta;
  const list = camper.preferences || [];
  const set = new Set(list);
  const rankBySkill = Object.create(null);
  for (let i = 0; i < list.length; i++) {
    const skill = list[i];
    if (rankBySkill[skill] === undefined) rankBySkill[skill] = i + 1;
  }
  meta = { set, rankBySkill };
  camperPreferenceCache.set(camper, meta);
  return meta;
}

function camperHasPreference(camper, skill) {
  return getCamperPreferenceMeta(camper).set.has(skill);
}

function createAssignedSkillSet() {
  const set = new Set();
  set._counts = Object.create(null);
  return set;
}

function buildStaffFirstLayout(input, rand, precomputed = null) {
  const campers = input.campers || [];
  const staffers = input.staffers || [];
  const settings = input.settings || {};
  const skillsConfig = input.skillsConfig || {};
  const groupMin = settings.groupMin ?? 10;
  const groupMax = settings.groupMax ?? 25;
  const oneEightRotation = settings.oneEightRotation ?? 4;
  const requiredOneEightRotations = getSkillFixedRotations(ONE_EIGHT, skillsConfig);
  const oneEightRotations = requiredOneEightRotations.length > 0 ? requiredOneEightRotations : [oneEightRotation];

  let demandBySkill = precomputed && precomputed.demandBySkill;
  let demandBySkillTribe = precomputed && precomputed.demandBySkillTribe;
  let weightedDemandBySkill = precomputed && precomputed.weightedDemandBySkill;
  let weightedDemandBySkillTribe = precomputed && precomputed.weightedDemandBySkillTribe;
  if (!demandBySkill || !demandBySkillTribe) {
    const tables = computeDemandTables(campers, settings, skillsConfig);
    demandBySkill = tables.demandBySkill;
    demandBySkillTribe = tables.demandBySkillTribe;
    weightedDemandBySkill = tables.weightedDemandBySkill;
    weightedDemandBySkillTribe = tables.weightedDemandBySkillTribe;
  }
  let teachersPerSkill = precomputed && precomputed.teachersPerSkill;
  if (!teachersPerSkill) {
    teachersPerSkill = {};
    Object.keys(demandBySkill).forEach((skill) => {
      teachersPerSkill[skill] = staffers.filter((s) => staffCanTeach(s, skill)).length;
    });
    teachersPerSkill[ONE_EIGHT] = staffers.filter((s) => staffCanTeach(s, ONE_EIGHT)).length;
    teachersPerSkill[MASU_MADNESS] = staffers.filter((s) => staffCanTeach(s, MASU_MADNESS)).length;
  }

  const plannedCapacityBySkillWindow = {};
  const plannedGroupsBySkillWindow = {};
  const addPlannedCapacity = (skill, preferredTribes) => {
    const key = getWindowKey(preferredTribes);
    if (!plannedCapacityBySkillWindow[skill]) {
      plannedCapacityBySkillWindow[skill] = {};
    }
    plannedCapacityBySkillWindow[skill][key] = (plannedCapacityBySkillWindow[skill][key] || 0) + groupMax;
    if (!plannedGroupsBySkillWindow[skill]) {
      plannedGroupsBySkillWindow[skill] = {};
    }
    plannedGroupsBySkillWindow[skill][key] = (plannedGroupsBySkillWindow[skill][key] || 0) + 1;
  };

  const groups = Array.from({ length: ROTATION_COUNT }, () => ({}));
  const staffAssignments = {};
  staffers.forEach((s) => {
    staffAssignments[s.name] = Array(ROTATION_COUNT).fill(null);
  });

  const oneEightTeacher = skillsConfig[ONE_EIGHT]?.requiredForEligible
    ? null
    : findOneEightTeacher(staffers, rand);

  const demandKeys = Object.keys(demandBySkill);
  if (!demandKeys.includes(MASU_MADNESS)) demandKeys.push(MASU_MADNESS);

  for (let r = 0; r < ROTATION_COUNT; r++) {
    const rotationStaffers = shuffleInPlace(staffers.slice(), rand);
    rotationStaffers.forEach((staffer, staffIndex) => {
      let assignedSkill = null;
      let preferredTribes = null;

      if (oneEightTeacher && staffer.name === oneEightTeacher.name && oneEightRotations.includes(r)) {
        assignedSkill = ONE_EIGHT;
        preferredTribes = ['Masu'];
      } else {
        const candidateSkills = [];
        (staffer.skills || []).forEach((skill, skillPriority) => {
          if (skill === ONE_EIGHT && !skillsConfig[skill]?.requiredForEligible) {
            if (oneEightRotations.includes(r)) candidateSkills.push({ skill, skillPriority });
            return;
          }
          if (!skillsConfig[skill]?.requiredForEligible && skillAllowsRotation(skill, r, skillsConfig)) {
            candidateSkills.push({ skill, skillPriority });
          }
        });

        let bestScore = -Infinity;
        let bestChoices = [];
        candidateSkills.forEach(({ skill, skillPriority }) => {
          const windows = getSkillEligibleTribeWindows(skill, settings, skillsConfig);
          windows.forEach((window) => {
            const windowKey = getWindowKey(window);
            const demand = window.reduce((sum, tribe) => sum + ((demandBySkillTribe[skill]?.[tribe]) || 0), 0);
            const weightedDemand = window.reduce((sum, tribe) => sum + ((weightedDemandBySkillTribe?.[skill]?.[tribe]) || 0), 0);
            if (demand <= 0) return;
            const planned = (plannedCapacityBySkillWindow[skill]?.[windowKey]) || 0;
            const plannedGroups = (plannedGroupsBySkillWindow[skill]?.[windowKey]) || 0;
            const skillMin = getSkillMinSize(skill, groupMin, skillsConfig, r);
            if (plannedGroups === 0 && demand < skillMin) return;
            const projectedGroups = plannedGroups + 1;
            const projectedAvg = demand / projectedGroups;
            if (projectedAvg < skillMin) return;
            const gap = demand - planned;
            const weightedCapacity = planned * getPreferenceDemandWeight(1);
            const weightedGap = weightedDemand - weightedCapacity;
            const scarcity = 1 / Math.max(1, teachersPerSkill[skill] || 1);
            const totalDemand = demandBySkill[skill] || 0;
            const totalWeightedDemand = weightedDemandBySkill?.[skill] || 0;
            const oversupplyPenalty = Math.max(0, planned - demand);
            const primarySkillBonus = Math.max(0, 220 - (skillPriority * 140));
            const score = weightedGap * 140 + gap * 150 + totalWeightedDemand * 2 + totalDemand * 5 + scarcity * 10 + primarySkillBonus - (oversupplyPenalty * 40);
            if (score > bestScore) {
              bestScore = score;
              bestChoices = [{ skill, preferredTribes: window }];
            } else if (score === bestScore) {
              bestChoices.push({ skill, preferredTribes: window });
            }
          });
        });
        if (bestChoices.length > 0) {
          const picked = randomChoice(bestChoices, rand);
          assignedSkill = picked.skill;
          preferredTribes = picked.preferredTribes;
        }
      }

      if (!assignedSkill) return;
      const baseKey = `${assignedSkill}__${staffer.name.replace(/\s+/g, '_')}`;
      const uniqueKey = groups[r][baseKey] ? `${baseKey}_${staffIndex + 1}` : baseKey;
      groups[r][uniqueKey] = {
        skill: assignedSkill,
        enabled: true,
        maxSize: getSkillMaxSize(assignedSkill, groupMax, skillsConfig, r),
        allowedGradeMin: null,
        allowedGradeMax: null,
        preferredTribes: Array.isArray(preferredTribes) ? preferredTribes.slice() : [],
        campers: [],
        tribes: {},
        staff: staffer.name,
      };
      staffAssignments[staffer.name][r] = uniqueKey;
      addPlannedCapacity(assignedSkill, preferredTribes || []);
    });
  }

  // Ensure ONE EIGHT exists in all configured required rotations if any teacher exists.
  if (oneEightTeacher) {
    const seenByRotation = new Set();
    for (let r = 0; r < groups.length; r++) {
      Object.keys(groups[r]).forEach((gKey) => {
        if (groups[r][gKey].skill === ONE_EIGHT) {
          seenByRotation.add(r);
        }
      });
    }
    oneEightRotations.forEach((rotation) => {
      if (seenByRotation.has(rotation)) return;
      const key = `${ONE_EIGHT}__${oneEightTeacher.name.replace(/\s+/g, '_')}_${rotation + 1}`;
      groups[rotation][key] = {
        skill: ONE_EIGHT,
        enabled: true,
        maxSize: getSkillMaxSize(ONE_EIGHT, groupMax, skillsConfig, rotation),
        allowedGradeMin: null,
        allowedGradeMax: null,
        preferredTribes: ['Masu'],
        campers: [],
        tribes: {},
        staff: oneEightTeacher.name,
      };
      staffAssignments[oneEightTeacher.name][rotation] = key;
    });
  }

  return { groups, staffAssignments, demandBySkill, demandBySkillTribe };
}

function cloneGroupLayout(layoutGroups) {
  return layoutGroups.map((rotationGroups) => {
    const copy = {};
    Object.keys(rotationGroups).forEach((gKey) => {
      const g = rotationGroups[gKey];
      copy[gKey] = {
        ...g,
        allowedGrades: Array.isArray(g.allowedGrades) ? g.allowedGrades.slice() : g.allowedGrades,
        preferredTribes: Array.isArray(g.preferredTribes) ? g.preferredTribes.slice() : [],
        campers: [],
        tribes: {},
        staff: g.staff || null,
      };
    });
    return copy;
  });
}

function buildGroupIndex(groups) {
  return groups.map((rotationGroups) => {
    const bySkill = {};
    Object.keys(rotationGroups).forEach((gKey) => {
      const skill = rotationGroups[gKey].skill;
      if (!bySkill[skill]) bySkill[skill] = [];
      bySkill[skill].push(gKey);
    });
    return bySkill;
  });
}

function isGroupEnabled(group) {
  return !!group && group.enabled !== false;
}

function getExistingGroupTribes(group) {
  return Object.keys(group?.tribes || {}).filter((tribe) => (group.tribes?.[tribe] || 0) > 0);
}

function canJoinPreferredTribeWindow(camper, group) {
  const preferredTribes = Array.isArray(group?.preferredTribes) ? group.preferredTribes : [];
  if (preferredTribes.length === 0) return true;
  if (getExistingGroupTribes(group).length > 0) return true;
  return preferredTribes.includes(camper.tribe);
}

function canJoinGroup(camper, group, assignedSkillSet, skill, skillsConfig, options = {}) {
  if (!isGroupEnabled(group)) return false;
  if (group.campers.length >= group.maxSize) return false;
  const currentCount = assignedSkillSet?._counts?.[skill] || (assignedSkillSet?.has(skill) ? 1 : 0);
  if (!options.allowRepeat && currentCount >= getSkillRepeatLimit(skill, camper, skillsConfig)) return false;
  if (!options.ignorePreference && !camperHasPreference(camper, skill)) return false;
  if (!isSkillAllowedForCamper(skill, camper, skillsConfig)) return false;
  if (!canJoinPreferredTribeWindow(camper, group)) return false;
  if (!canGroupTribes(getExistingGroupTribes(group), camper.tribe, options.locationTribes || createDefaultTribes())) return false;
  return true;
}

function canJoinGroupIgnoringCapacity(camper, group, assignedSkillSet, skill, skillsConfig, options = {}) {
  if (!isGroupEnabled(group)) return false;
  const currentCount = assignedSkillSet?._counts?.[skill] || (assignedSkillSet?.has(skill) ? 1 : 0);
  if (!options.allowRepeat && currentCount >= getSkillRepeatLimit(skill, camper, skillsConfig)) return false;
  if (!options.ignorePreference && !camperHasPreference(camper, skill)) return false;
  if (!isSkillAllowedForCamper(skill, camper, skillsConfig)) return false;
  if (!canJoinPreferredTribeWindow(camper, group)) return false;
  if (!canGroupTribes(getExistingGroupTribes(group), camper.tribe, options.locationTribes || createDefaultTribes())) return false;
  return true;
}

function assignCamperToGroup(camper, rotation, groupKey, groups, assignments, assignedSkillMap) {
  const group = groups[rotation][groupKey];
  if (!group) return false;
  const camperKey = getCamperAssignmentKey(camper);
  if (!assignments[camperKey]) assignments[camperKey] = {};
  assignments[camperKey][rotation] = groupKey;
  if (!assignedSkillMap[camperKey]) assignedSkillMap[camperKey] = createAssignedSkillSet();
  const assignedSet = assignedSkillMap[camperKey];
  if (!assignedSet._counts) assignedSet._counts = Object.create(null);
  assignedSet._counts[group.skill] = (assignedSet._counts[group.skill] || 0) + 1;
  assignedSet.add(group.skill);
  group.campers.push(camper);
  group.tribes[camper.tribe] = (group.tribes[camper.tribe] || 0) + 1;
  return true;
}

function removeCamperFromGroup(camper, rotation, groupKey, groups, assignments, assignedSkillMap) {
  const group = groups[rotation] && groups[rotation][groupKey];
  if (!group) return;
  const camperKey = getCamperAssignmentKey(camper);
  const campers = group.campers || [];
  for (let i = 0; i < campers.length; i++) {
    if (campers[i] && getCamperAssignmentKey(campers[i]) === camperKey) {
      campers.splice(i, 1);
      break;
    }
  }
  group.tribes[camper.tribe] = (group.tribes[camper.tribe] || 1) - 1;
  if (group.tribes[camper.tribe] <= 0) delete group.tribes[camper.tribe];
  if (assignments[camperKey] && assignments[camperKey][rotation] !== undefined) {
    delete assignments[camperKey][rotation];
  }
  const skill = group.skill;
  const assignedSet = assignedSkillMap[camperKey];
  const counts = assignedSet && assignedSet._counts;
  if (assignedSet && counts) {
    counts[skill] = (counts[skill] || 1) - 1;
    if (counts[skill] <= 0) {
      delete counts[skill];
      assignedSet.delete(skill);
    }
    return;
  }
  const hasSkillElsewhere = Object.keys(assignments[camperKey] || {}).some((rStr) => {
    const r = Number(rStr);
    const gKey = assignments[camperKey][r];
    const g = groups[r] && groups[r][gKey];
    return g && g.skill === skill;
  });
  if (!hasSkillElsewhere && assignedSet) {
    assignedSet.delete(skill);
  }
}

function initializeAssignmentState(campers) {
  const assignments = {};
  const assignedSkillMap = {};
  campers.forEach((c) => {
    const camperKey = getCamperAssignmentKey(c);
    assignments[camperKey] = {};
    assignedSkillMap[camperKey] = createAssignedSkillSet();
  });
  return { assignments, assignedSkillMap };
}

function preferenceRank(camper, skill) {
  const rank = getCamperPreferenceMeta(camper).rankBySkill[skill];
  return rank === undefined ? 11 : rank;
}

function dynamicLowDemandSkills(demandBySkill, groupMin) {
  const skills = Object.keys(demandBySkill);
  if (skills.length === 0) return new Set();
  const threshold = Math.max(2, Math.floor(groupMin / 3));
  return new Set(skills.filter((skill) => (demandBySkill[skill] || 0) <= threshold));
}

function buildDemandRankBySkill(demandBySkill) {
  const sorted = Object.keys(demandBySkill || {})
    .sort((a, b) => (demandBySkill[a] || 0) - (demandBySkill[b] || 0) || a.localeCompare(b));
  const ranks = {};
  sorted.forEach((skill, idx) => {
    ranks[skill] = idx + 1;
  });
  return ranks;
}

function buildStaffSkillPriority(staffers = []) {
  const priority = {};
  staffers.forEach((staffer) => {
    if (!staffer?.name) return;
    priority[staffer.name] = {};
    (staffer.skills || []).forEach((skill, idx) => {
      if (priority[staffer.name][skill] === undefined) {
        priority[staffer.name][skill] = idx;
      }
    });
  });
  return priority;
}

function buildConstrainedDemandSkills(demandBySkill = {}, weightedDemandBySkill = {}, teachersPerSkill = {}, groupMax = 25) {
  return Object.keys(demandBySkill || {})
    .filter((skill) => {
      const demand = demandBySkill[skill] || 0;
      const weightedDemand = weightedDemandBySkill[skill] || 0;
      const teacherCount = teachersPerSkill[skill] || 0;
      if (demand <= 0 || teacherCount <= 0) return false;
      const roughCapacity = Math.max(1, teacherCount * ROTATION_COUNT * groupMax);
      return weightedDemand >= groupMax * getPreferenceDemandWeight(2)
        || demand > roughCapacity * 0.55
        || weightedDemand > roughCapacity * getPreferenceDemandWeight(3);
    })
    .sort((a, b) => {
      const weightedA = weightedDemandBySkill[a] || 0;
      const weightedB = weightedDemandBySkill[b] || 0;
      if (weightedA !== weightedB) return weightedB - weightedA;
      return (demandBySkill[b] || 0) - (demandBySkill[a] || 0) || a.localeCompare(b);
    });
}

export function buildStaticOptimizerContext(input) {
  const campers = input.campers || [];
  const staffers = input.staffers || [];
  const groupMin = (input.settings || {}).groupMin ?? 10;
  const {
    demandBySkill,
    demandBySkillTribe,
    preferenceDemandBySkill,
    preferenceDemandBySkillTribe,
    eligiblePreferenceDemandBySkill,
    eligiblePreferenceDemandBySkillTribe,
    weightedDemandBySkill,
    weightedDemandBySkillTribe,
    weightedPreferenceDemandBySkill,
    weightedPreferenceDemandBySkillTribe,
    weightedEligiblePreferenceDemandBySkill,
    weightedEligiblePreferenceDemandBySkillTribe,
    requiredDemandBySkill,
    requiredDemandBySkillTribe,
    rankDemandBySkill,
  } = computeDemandTables(campers, input.settings || {}, input.skillsConfig || {});
  const demandRankBySkill = buildDemandRankBySkill(preferenceDemandBySkill);
  const teachersPerSkill = {};
  Object.keys({ ...demandBySkill, ...preferenceDemandBySkill, ...requiredDemandBySkill }).forEach((skill) => {
    teachersPerSkill[skill] = staffers.filter((s) => staffCanTeach(s, skill)).length;
  });
  teachersPerSkill[ONE_EIGHT] = staffers.filter((s) => staffCanTeach(s, ONE_EIGHT)).length;
  teachersPerSkill[MASU_MADNESS] = staffers.filter((s) => staffCanTeach(s, MASU_MADNESS)).length;
  const lowDemandSet = dynamicLowDemandSkills(preferenceDemandBySkill, groupMin);
  const constrainedDemandSkills = buildConstrainedDemandSkills(
    preferenceDemandBySkill,
    weightedPreferenceDemandBySkill,
    teachersPerSkill,
    (input.settings || {}).groupMax ?? 25
  );
  const skillsByDemandAsc = Object.keys(preferenceDemandBySkill || {})
    .sort((a, b) => (preferenceDemandBySkill[a] || 0) - (preferenceDemandBySkill[b] || 0) || a.localeCompare(b));
  const scarceSkillCount = Math.max(1, Math.floor(skillsByDemandAsc.length / 3));
  const scarceSkills = skillsByDemandAsc.slice(0, scarceSkillCount);
  const scarcityPenaltySkills = new Set(scarceSkills);
  return {
    demandBySkill,
    demandBySkillTribe,
    preferenceDemandBySkill,
    preferenceDemandBySkillTribe,
    eligiblePreferenceDemandBySkill,
    eligiblePreferenceDemandBySkillTribe,
    weightedDemandBySkill,
    weightedDemandBySkillTribe,
    weightedPreferenceDemandBySkill,
    weightedPreferenceDemandBySkillTribe,
    weightedEligiblePreferenceDemandBySkill,
    weightedEligiblePreferenceDemandBySkillTribe,
    requiredDemandBySkill,
    requiredDemandBySkillTribe,
    rankDemandBySkill,
    demandRankBySkill,
    teachersPerSkill,
    staffSkillPriority: buildStaffSkillPriority(staffers),
    lowDemandSet,
    constrainedDemandSkills,
    scarceSkills,
    scarcityPenaltySkills,
  };
}

function tryAssignRequested(
  camper,
  rotations,
  groups,
  groupsBySkill,
  assignments,
  assignedSkillMap,
  skillsConfig,
  rand,
  demandBySkill = {},
  demandRankBySkill = {}
) {
  const camperKey = getCamperAssignmentKey(camper);
  const camperAssigned = assignments[camperKey] || {};
  const assignedSet = assignedSkillMap[camperKey] || new Set();
  let placedAny = false;
  const rotationOrder = shuffleInPlace(rotations.slice(), rand);
  rotationOrder.forEach((r) => {
    if (camperAssigned[r] !== undefined) return;
    const candidates = [];
    (camper.preferences || []).forEach((skill) => {
      if (hasReachedSkillLimit(assignedSet, camper, skill, skillsConfig)) return;
      const gKeys = (groupsBySkill[r] && groupsBySkill[r][skill]) || [];
      gKeys.forEach((gKey) => {
        const group = groups[r][gKey];
        if (!canJoinGroup(camper, group, assignedSet, skill, skillsConfig)) return;
        const rank = preferenceRank(camper, skill);
        const demandRank = demandRankBySkill[skill] || 999;
        const demand = demandBySkill[skill] || 0;
        const fillRatio = group.maxSize > 0 ? (group.campers.length / group.maxSize) : 0;
        // Strongly avoid saturating groups and bias toward scarce-skill assignments first.
        const saturationPenalty = Math.round((fillRatio ** 3) * 180);
        const score = rank * 95 + demandRank * 4 + demand * 0.05 + group.campers.length * 2 + saturationPenalty + rand();
        candidates.push({ gKey, score });
      });
    });
    if (candidates.length === 0) return;
    candidates.sort((a, b) => a.score - b.score);
    assignCamperToGroup(camper, r, candidates[0].gKey, groups, assignments, assignedSkillMap);
    placedAny = true;
  });
  return placedAny;
}

function findBestRotationReplacement(camper, rotation, groups, assignments, assignedSkillMap, skillsConfig, rand, excludedGroupKey) {
  const camperKey = getCamperAssignmentKey(camper);
  const assignedSet = assignedSkillMap[camperKey] || new Set();
  const candidates = [];
  Object.keys(groups[rotation] || {}).forEach((gKey) => {
    if (gKey === excludedGroupKey) return;
    const group = groups[rotation][gKey];
    if (!isGroupEnabled(group)) return;
    const skill = group.skill;
    if (!canJoinGroup(camper, group, assignedSet, skill, skillsConfig)) return;
    const score = preferenceRank(camper, skill) * 100 + group.campers.length * 3 + rand();
    candidates.push({ gKey, score });
  });
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.score - b.score);
  return candidates[0].gKey;
}

function disableGroup(group) {
  if (!group) return;
  group.enabled = false;
  group.campers = [];
  group.tribes = {};
}

function mergeDuplicateSkillGroupsInRotation(rotation, groups, assignments, assignedSkillMap, skillsConfig, rand) {
  const rotationGroups = groups[rotation] || {};
  const bySkill = {};
  Object.keys(rotationGroups).forEach((gKey) => {
    const group = rotationGroups[gKey];
    if (!isGroupEnabled(group)) return;
    if ((group.campers || []).length === 0) return;
    if (!bySkill[group.skill]) bySkill[group.skill] = [];
    bySkill[group.skill].push(gKey);
  });

  let changed = false;
  Object.keys(bySkill).forEach((skill) => {
    const keys = bySkill[skill]
      .slice()
      .sort((a, b) => rotationGroups[b].campers.length - rotationGroups[a].campers.length);
    if (keys.length <= 1) return;
    for (let i = keys.length - 1; i >= 1; i--) {
      const srcKey = keys[i];
      const src = rotationGroups[srcKey];
      if (!isGroupEnabled(src) || src.campers.length === 0) continue;
      const targetKeys = keys
        .filter((k) => k !== srcKey)
        .sort((a, b) => rotationGroups[a].campers.length - rotationGroups[b].campers.length);
      const movers = src.campers.slice();
      movers.forEach((camper) => {
        const camperKey = getCamperAssignmentKey(camper);
        let moved = false;
        for (const dstKey of targetKeys) {
          const dst = rotationGroups[dstKey];
          if (!isGroupEnabled(dst)) continue;
          if (dst.campers.length >= dst.maxSize) continue;
          removeCamperFromGroup(camper, rotation, srcKey, groups, assignments, assignedSkillMap);
          const assignedSet = assignedSkillMap[camperKey] || new Set();
          if (canJoinGroup(camper, dst, assignedSet, dst.skill, skillsConfig)) {
            assignCamperToGroup(camper, rotation, dstKey, groups, assignments, assignedSkillMap);
            moved = true;
            changed = true;
            break;
          }
          // Roll back if this destination did not work.
          assignCamperToGroup(camper, rotation, srcKey, groups, assignments, assignedSkillMap);
        }
        if (!moved) {
          // Camper stays in source when no destination can accept them.
        }
      });
      if (src.campers.length === 0) {
        disableGroup(src);
        changed = true;
      }
    }
  });
  return changed;
}

function enforceHardGroupMinimums(ctx) {
  const {
    campers,
    groups,
    assignments,
    assignedSkillMap,
    skillsConfig,
    rand,
    groupMin,
    counters,
    demandBySkill,
    demandRankBySkill,
  } = ctx;

  if (!Number.isFinite(groupMin) || groupMin <= 1) return false;
  let overallChanged = false;
  const allRotations = Array.from({ length: ROTATION_COUNT }, (_, i) => i);
  const rescueUnderMinGroup = (rotation, gKey) => {
    const group = groups[rotation] && groups[rotation][gKey];
    if (!isGroupEnabled(group)) return false;
    const skillMin = getSkillMinSize(group.skill, groupMin, skillsConfig, rotation);
    if ((group.campers || []).length === 0 || group.campers.length >= skillMin) return false;
    let changed = false;

    const openRequesters = campers
      .filter((camper) => {
        const camperKey = getCamperAssignmentKey(camper);
        if ((assignments[camperKey] || {})[rotation] !== undefined) return false;
        return canJoinGroup(camper, group, assignedSkillMap[camperKey], group.skill, skillsConfig);
      })
      .sort((a, b) => preferenceRank(a, group.skill) - preferenceRank(b, group.skill) || a.name.localeCompare(b.name));
    for (const camper of openRequesters) {
      if (group.campers.length >= skillMin || group.campers.length >= group.maxSize) break;
      assignCamperToGroup(camper, rotation, gKey, groups, assignments, assignedSkillMap);
      changed = true;
      if (counters) counters.hardMinRescuePlacements = (counters.hardMinRescuePlacements || 0) + 1;
    }
    if (group.campers.length >= skillMin) {
      if (counters) counters.hardMinGroupsRescued = (counters.hardMinGroupsRescued || 0) + 1;
      return true;
    }

    const donorKeys = Object.keys(groups[rotation] || {})
      .filter((key) => key !== gKey)
      .filter((key) => {
        const donor = groups[rotation][key];
        if (!donor || !isGroupEnabled(donor) || donor.skill !== group.skill) return false;
        const donorMin = getSkillMinSize(donor.skill, groupMin, skillsConfig, rotation);
        return (donor.campers || []).length > donorMin;
      })
      .sort((a, b) => groups[rotation][b].campers.length - groups[rotation][a].campers.length || a.localeCompare(b));

    for (const donorKey of donorKeys) {
      const donor = groups[rotation][donorKey];
      const donorMin = getSkillMinSize(donor.skill, groupMin, skillsConfig, rotation);
      const movers = donor.campers
        .slice()
        .sort((a, b) => preferenceRank(a, group.skill) - preferenceRank(b, group.skill) || a.name.localeCompare(b.name));
      for (const camper of movers) {
        if (group.campers.length >= skillMin || group.campers.length >= group.maxSize) break;
        if (donor.campers.length <= donorMin) break;
        const camperKey = getCamperAssignmentKey(camper);
        removeCamperFromGroup(camper, rotation, donorKey, groups, assignments, assignedSkillMap);
        if (canJoinGroup(camper, group, assignedSkillMap[camperKey], group.skill, skillsConfig)) {
          assignCamperToGroup(camper, rotation, gKey, groups, assignments, assignedSkillMap);
          changed = true;
          if (counters) counters.hardMinRescueRebalances = (counters.hardMinRescueRebalances || 0) + 1;
        } else {
          assignCamperToGroup(camper, rotation, donorKey, groups, assignments, assignedSkillMap);
        }
      }
      if (group.campers.length >= skillMin) {
        if (counters) counters.hardMinGroupsRescued = (counters.hardMinGroupsRescued || 0) + 1;
        return true;
      }
    }
    return changed;
  };
  let pass = 0;
  while (pass < 8) {
    let changed = false;
    for (let r = 0; r < ROTATION_COUNT; r++) {
      if (mergeDuplicateSkillGroupsInRotation(r, groups, assignments, assignedSkillMap, skillsConfig, rand)) {
        changed = true;
      }
    }

    const underMinGroups = [];
    groups.forEach((rotationGroups, rotation) => {
      Object.keys(rotationGroups || {}).forEach((gKey) => {
        const group = rotationGroups[gKey];
        if (!isGroupEnabled(group)) return;
        const size = (group.campers || []).length;
        const skillMin = getSkillMinSize(group.skill, groupMin, skillsConfig, rotation);
        if (size > 0 && size < skillMin) {
          underMinGroups.push({ rotation, gKey, size });
        }
      });
    });

    if (underMinGroups.length === 0) {
      overallChanged = overallChanged || changed;
      break;
    }

    underMinGroups.sort((a, b) => a.size - b.size);
    underMinGroups.forEach(({ rotation, gKey }) => {
      const group = groups[rotation] && groups[rotation][gKey];
      if (!isGroupEnabled(group)) return;
      const size = (group.campers || []).length;
      const skillMin = getSkillMinSize(group.skill, groupMin, skillsConfig, rotation);
      if (size === 0 || size >= skillMin) return;
      if (rescueUnderMinGroup(rotation, gKey)) {
        const rescuedGroup = groups[rotation] && groups[rotation][gKey];
        if (rescuedGroup && (rescuedGroup.campers || []).length >= skillMin) {
          changed = true;
          return;
        }
      }

      const movers = group.campers.slice();
      movers.forEach((camper) => {
        removeCamperFromGroup(camper, rotation, gKey, groups, assignments, assignedSkillMap);
        const replacement = findBestRotationReplacement(
          camper,
          rotation,
          groups,
          assignments,
          assignedSkillMap,
          skillsConfig,
          rand,
          gKey
        );
        if (replacement) {
          assignCamperToGroup(camper, rotation, replacement, groups, assignments, assignedSkillMap);
          if (counters) counters.hardMinReassignments += 1;
        } else if (counters) {
          counters.hardMinDrops += 1;
        }
      });

      disableGroup(group);
      if (counters) counters.hardMinGroupsDisabled += 1;
      changed = true;
    });

    // Refill any newly opened holes into still-enabled groups only.
    const groupsBySkill = buildGroupIndex(groups);
    let refillProgress = true;
    let refillPass = 0;
    while (refillProgress && refillPass < 6) {
      refillProgress = false;
      const order = shuffleInPlace(campers.slice(), rand);
      order.forEach((camper) => {
        if (tryAssignRequested(
          camper,
          allRotations,
          groups,
          groupsBySkill,
          assignments,
          assignedSkillMap,
          skillsConfig,
          rand,
          demandBySkill,
          demandRankBySkill
        )) {
          refillProgress = true;
        }
      });
      refillPass += 1;
    }

    overallChanged = overallChanged || changed;
    if (!changed) break;
    pass += 1;
  }

  return overallChanged;
}

function fillByPreferenceRounds(ctx) {
  const {
    campers,
    groups,
    groupsBySkill,
    assignments,
    assignedSkillMap,
    skillsConfig,
    rand,
    demandBySkill,
    demandRankBySkill,
    groupMin,
    lowDemandSet: precomputedLowDemandSet,
    constrainedDemandSkills: precomputedConstrainedDemandSkills,
  } = ctx;

  const lowDemandSet = precomputedLowDemandSet || dynamicLowDemandSkills(demandBySkill, groupMin);
  const constrainedDemandSkills = precomputedConstrainedDemandSkills || [];
  const feasibleLowDemandSet = new Set(Array.from(lowDemandSet).filter((skill) => {
    const preferenceDemand = demandBySkill[skill] || 0;
    if (preferenceDemand <= 0) return false;
    const allowedRotations = Array.from({ length: ROTATION_COUNT }, (_, idx) => idx)
      .filter((rotation) => skillAllowsRotation(skill, rotation, skillsConfig));
    if (allowedRotations.length === 0) return false;
    const lowestMin = Math.min(...allowedRotations.map((rotation) => getSkillMinSize(skill, groupMin, skillsConfig, rotation)));
    return preferenceDemand >= lowestMin;
  }));

  const requiredSkills = Object.keys(skillsConfig || {}).filter((skill) => skillsConfig[skill]?.requiredForEligible);
  requiredSkills.forEach((skill) => {
    if (getRequiredSchedule(skillsConfig[skill]).mode === REQUIRED_SCHEDULE_MODE_PER_BLOCK) return;
    const eligibleCampers = campers.filter((camper) => isSkillAllowedForCamper(skill, camper, skillsConfig));
    eligibleCampers.sort((a, b) => {
      const openA = ROTATION_COUNT - Object.keys(assignments[getCamperAssignmentKey(a)] || {}).length;
      const openB = ROTATION_COUNT - Object.keys(assignments[getCamperAssignmentKey(b)] || {}).length;
      if (openA !== openB) return openA - openB;
      return a.name.localeCompare(b.name);
    });
    eligibleCampers.forEach((camper) => {
      const camperKey = getCamperAssignmentKey(camper);
      const fixedRotations = getSkillFixedRotations(skill, skillsConfig);
      const rotations = fixedRotations.length > 0
        ? fixedRotations.slice()
        : Array.from({ length: ROTATION_COUNT }, (_, i) => i);
      rotations.forEach((r) => {
        if (hasReachedSkillLimit(assignedSkillMap[camperKey], camper, skill, skillsConfig)) return;
        if (assignments[camperKey][r] !== undefined) return;
        const keys = (groupsBySkill[r] && groupsBySkill[r][skill]) || [];
        const sorted = keys.slice().sort((a, b) => groups[r][a].campers.length - groups[r][b].campers.length);
        for (const gKey of sorted) {
          if (!canJoinGroup(camper, groups[r][gKey], assignedSkillMap[camperKey], skill, skillsConfig, { ignorePreference: true })) continue;
          assignCamperToGroup(camper, r, gKey, groups, assignments, assignedSkillMap);
          break;
        }
      });
    });
  });

  // Constrained high-demand first pass.
  // This protects limited seats for campers who ranked popular, capacity-limited skills highly.
  constrainedDemandSkills.forEach((skill) => {
    const requesters = campers
      .filter((camper) => {
        const camperKey = getCamperAssignmentKey(camper);
        return camperHasPreference(camper, skill) && !hasReachedSkillLimit(assignedSkillMap[camperKey], camper, skill, skillsConfig);
      });
    // Prioritize campers with fewer open rotations first (MRV-style).
    requesters.sort((a, b) => {
      const openA = ROTATION_COUNT - Object.keys(assignments[getCamperAssignmentKey(a)] || {}).length;
      const openB = ROTATION_COUNT - Object.keys(assignments[getCamperAssignmentKey(b)] || {}).length;
      if (openA !== openB) return openA - openB;
      const prefA = preferenceRank(a, skill);
      const prefB = preferenceRank(b, skill);
      if (prefA !== prefB) return prefA - prefB;
      return a.name.localeCompare(b.name);
    });
    requesters.forEach((camper) => {
      const camperKey = getCamperAssignmentKey(camper);
      if (hasReachedSkillLimit(assignedSkillMap[camperKey], camper, skill, skillsConfig)) return;
      const slotCandidates = [];
      for (let r = 0; r < ROTATION_COUNT; r++) {
        if (assignments[camperKey][r] !== undefined) continue;
        const keys = (groupsBySkill[r] && groupsBySkill[r][skill]) || [];
        keys.forEach((gKey) => {
          const group = groups[r][gKey];
          if (!canJoinGroup(camper, group, assignedSkillMap[camperKey], skill, skillsConfig)) return;
          const fillRatio = group.maxSize > 0 ? (group.campers.length / group.maxSize) : 0;
          const saturationPenalty = Math.round((fillRatio ** 3) * 250);
          const score = preferenceRank(camper, skill) * 80 + group.campers.length * 8 + saturationPenalty + rand();
          slotCandidates.push({ r, gKey, score });
        });
      }
      if (slotCandidates.length === 0) return;
      slotCandidates.sort((a, b) => a.score - b.score);
      const chosen = slotCandidates[0];
      assignCamperToGroup(camper, chosen.r, chosen.gKey, groups, assignments, assignedSkillMap);
    });
  });

  // Low-demand guarantee first (dynamic).
  campers.forEach((camper) => {
    const camperKey = getCamperAssignmentKey(camper);
    const requestedLow = (camper.preferences || []).filter((skill) => feasibleLowDemandSet.has(skill));
    requestedLow.forEach((skill) => {
      if (hasReachedSkillLimit(assignedSkillMap[camperKey], camper, skill, skillsConfig)) return;
      for (let r = 0; r < ROTATION_COUNT; r++) {
        if (assignments[camperKey][r] !== undefined) continue;
        const keys = (groupsBySkill[r] && groupsBySkill[r][skill]) || [];
        const sorted = keys.slice().sort((a, b) => groups[r][a].campers.length - groups[r][b].campers.length);
        let placed = false;
        for (const gKey of sorted) {
          if (!canJoinGroup(camper, groups[r][gKey], assignedSkillMap[camperKey], skill, skillsConfig)) continue;
          assignCamperToGroup(camper, r, gKey, groups, assignments, assignedSkillMap);
          placed = true;
          break;
        }
        if (placed) break;
      }
    });
  });

  // Preference rounds.
  for (let pref = 1; pref <= 10; pref++) {
    const order = shuffleInPlace(campers.slice(), rand);
    order.forEach((camper) => {
      const skill = (camper.preferences || [])[pref - 1];
      if (!skill) return;
      const camperKey = getCamperAssignmentKey(camper);
      if (hasReachedSkillLimit(assignedSkillMap[camperKey], camper, skill, skillsConfig)) return;
      const rotations = shuffleInPlace(Array.from({ length: ROTATION_COUNT }, (_, i) => i), rand);
      for (const r of rotations) {
        if (assignments[camperKey][r] !== undefined) continue;
        const keys = (groupsBySkill[r] && groupsBySkill[r][skill]) || [];
        if (keys.length === 0) continue;
        const sorted = keys.slice().sort((a, b) => groups[r][a].campers.length - groups[r][b].campers.length);
        let placed = false;
        for (const gKey of sorted) {
          if (!canJoinGroup(camper, groups[r][gKey], assignedSkillMap[camperKey], skill, skillsConfig)) continue;
          assignCamperToGroup(camper, r, gKey, groups, assignments, assignedSkillMap);
          placed = true;
          break;
        }
        if (placed) break;
      }
    });
  }

  // Fill remaining holes with requested-only search across multiple passes.
  const rotationIndexes = Array.from({ length: ROTATION_COUNT }, (_, i) => i);
  let progress = true;
  let passes = 0;
  while (progress && passes < 8) {
    progress = false;
    const order = shuffleInPlace(campers.slice(), rand);
    order.forEach((camper) => {
      if (tryAssignRequested(
        camper,
        rotationIndexes,
        groups,
        groupsBySkill,
        assignments,
        assignedSkillMap,
        skillsConfig,
        rand,
        demandBySkill,
        demandRankBySkill
      )) {
        progress = true;
      }
    });
    passes += 1;
  }
}

function buildConstrainedDemandCommitments(
  groups,
  demandBySkill,
  demandBySkillTribe,
  weightedDemandBySkill,
  rankDemandBySkill,
  groupMin,
  groupMax,
  constrainedDemandSkills = []
) {
  if (!Number.isFinite(groupMin) || groupMin <= 0) return [];
  const groupsBySkill = {};
  const capacityBySkill = {};
  groups.forEach((rotationGroups, rotation) => {
    Object.keys(rotationGroups || {}).forEach((gKey) => {
      const group = rotationGroups[gKey];
      if (!group || !isGroupEnabled(group)) return;
      const skill = group.skill;
      if (!groupsBySkill[skill]) groupsBySkill[skill] = [];
      groupsBySkill[skill].push({ rotation, gKey, group });
      capacityBySkill[skill] = (capacityBySkill[skill] || 0) + Math.max(1, group.maxSize || groupMax);
    });
  });

  const plans = [];
  const candidateSkills = constrainedDemandSkills.length > 0
    ? constrainedDemandSkills
    : buildConstrainedDemandSkills(demandBySkill, weightedDemandBySkill, {}, groupMax);
  candidateSkills.forEach((skill) => {
    const entries = groupsBySkill[skill];
    if (!entries || entries.length === 0) return;
    const totalDemand = (demandBySkill && demandBySkill[skill]) || 0;
    if (totalDemand < groupMin) return;
    const weightedDemand = weightedDemandBySkill?.[skill] || 0;
    const topThreeDemand = (rankDemandBySkill?.[skill]?.[1] || 0)
      + (rankDemandBySkill?.[skill]?.[2] || 0)
      + (rankDemandBySkill?.[skill]?.[3] || 0);
    const weightedCapacity = (capacityBySkill[skill] || 0) * getPreferenceDemandWeight(1);
    const capacityShortfall = Math.max(0, weightedDemand - weightedCapacity);
    const sortedEntries = entries.slice().sort((a, b) => {
      const windowA = getWindowKey(a.group.preferredTribes || []);
      const windowB = getWindowKey(b.group.preferredTribes || []);
      const demandA = (a.group.preferredTribes || []).reduce((sum, tribe) => sum + ((demandBySkillTribe?.[skill]?.[tribe]) || 0), 0);
      const demandB = (b.group.preferredTribes || []).reduce((sum, tribe) => sum + ((demandBySkillTribe?.[skill]?.[tribe]) || 0), 0);
      if (demandA !== demandB) return demandB - demandA;
      return a.rotation - b.rotation || windowA.localeCompare(windowB) || a.gKey.localeCompare(b.gKey);
    });
    const targetRunsByDemand = Math.max(1, Math.ceil(Math.min(topThreeDemand || totalDemand, totalDemand) / Math.max(1, groupMax)));
    const targetRunsByShortfall = capacityShortfall > 0 ? sortedEntries.length : Math.ceil(totalDemand / Math.max(1, groupMax));
    const commitCount = Math.min(sortedEntries.length, Math.max(1, Math.min(targetRunsByDemand, targetRunsByShortfall)));
    const committedEntries = sortedEntries.slice(0, commitCount);

    if (committedEntries.length === 0) return;
    const requiredSeats = committedEntries.length * groupMin;
    const slack = totalDemand - requiredSeats;
    plans.push({
      skill,
      totalDemand,
      weightedDemand,
      capacityShortfall,
      topThreeDemand,
      slack,
      committedEntries,
    });
  });

  plans.sort((a, b) => {
    if (a.capacityShortfall !== b.capacityShortfall) return b.capacityShortfall - a.capacityShortfall;
    if (a.weightedDemand !== b.weightedDemand) return b.weightedDemand - a.weightedDemand;
    if (a.topThreeDemand !== b.topThreeDemand) return b.topThreeDemand - a.topThreeDemand;
    return a.skill.localeCompare(b.skill);
  });

  const commitments = [];
  plans.forEach((plan) => {
    plan.committedEntries.forEach((entry, idx) => {
      commitments.push({
        rotation: entry.rotation,
        gKey: entry.gKey,
        skill: plan.skill,
        targetSize: groupMin,
        slack: plan.slack,
        weightedDemand: plan.weightedDemand,
        capacityShortfall: plan.capacityShortfall,
        priority: idx,
      });
    });
  });
  return commitments;
}

function isProtectedProgramShapeGroup(group, rotation, settings, skillsConfig) {
  if (!group) return false;
  if (skillsConfig[group.skill]?.requiredForEligible) return true;
  if (group.skill !== ONE_EIGHT) return false;
  const fixedRotations = getSkillFixedRotations(ONE_EIGHT, skillsConfig);
  const requiredRotations = fixedRotations.length > 0
    ? fixedRotations
    : [settings.oneEightRotation ?? 4];
  return requiredRotations.includes(rotation);
}

function countDemandSupportedRuns(eligibleDemand, rotationOptions) {
  let supportedRuns = 0;
  let requiredSeats = 0;
  rotationOptions
    .slice()
    .sort((a, b) => a.minSize - b.minSize || a.rotation - b.rotation)
    .forEach((option) => {
      if (requiredSeats + option.minSize > eligibleDemand) return;
      requiredSeats += option.minSize;
      supportedRuns += 1;
    });
  return supportedRuns;
}

function computeProgramShapeTarget(eligibleDemand, rotationOptions, staffedRotationOptions) {
  if (rotationOptions.length === 0) {
    return { targetRuns: 0, targetRunReason: 'rotation-limited' };
  }

  const effectiveMin = Math.max(1, Math.min(...rotationOptions.map((option) => option.minSize)));
  const effectiveMax = Math.max(1, Math.max(...rotationOptions.map((option) => option.maxSize)));
  if (eligibleDemand < effectiveMin) {
    return {
      targetRuns: 0,
      targetRunReason: 'below-minimum-demand',
      effectiveMin,
      effectiveMax,
    };
  }

  let policyTargetRuns;
  let targetRunReason;
  if (eligibleDemand < effectiveMin * 5) {
    policyTargetRuns = Math.floor(eligibleDemand / effectiveMin);
    targetRunReason = 'reduced-by-demand';
  } else {
    policyTargetRuns = 5;
    targetRunReason = 'default-five';
  }

  const demandSupportedRuns = countDemandSupportedRuns(eligibleDemand, rotationOptions);
  const capacityPressureThreshold = rotationOptions
    .map((option) => option.maxSize)
    .sort((a, b) => b - a)
    .slice(0, policyTargetRuns)
    .reduce((sum, maxSize) => sum + Math.max(0, maxSize - 5), 0);
  if (
    policyTargetRuns > 0
    && demandSupportedRuns >= ROTATION_COUNT
    && eligibleDemand > capacityPressureThreshold
  ) {
    policyTargetRuns = ROTATION_COUNT;
    targetRunReason = 'expanded-for-capacity';
  }

  policyTargetRuns = Math.max(0, Math.min(ROTATION_COUNT, policyTargetRuns));
  let targetRuns = policyTargetRuns;
  if (rotationOptions.length < targetRuns) {
    targetRuns = rotationOptions.length;
    targetRunReason = 'rotation-limited';
  }
  if (demandSupportedRuns < targetRuns) {
    targetRuns = demandSupportedRuns;
    targetRunReason = 'reduced-by-demand';
  }

  const staffedDemandSupportedRuns = countDemandSupportedRuns(eligibleDemand, staffedRotationOptions);
  if (staffedDemandSupportedRuns < targetRuns) {
    targetRuns = staffedDemandSupportedRuns;
    targetRunReason = 'staff-limited';
  }

  return {
    targetRuns,
    targetRunReason,
    effectiveMin,
    effectiveMax,
  };
}

function buildProgramShapeTargetRunPlans(
  input,
  groups,
  assignments,
  assignedSkillMap,
  demandTables,
  teachersPerSkill = {}
) {
  const campers = input.campers || [];
  const staffers = input.staffers || [];
  const settings = input.settings || {};
  const skillsConfig = input.skillsConfig || {};
  const groupMin = settings.groupMin ?? 10;
  const groupMax = settings.groupMax ?? 25;
  const preferenceDemandBySkill = demandTables.preferenceDemandBySkill || {};
  const skillNames = Object.keys(preferenceDemandBySkill);

  const plans = skillNames.map((skill) => {
    if (skill === ONE_EIGHT || skillsConfig[skill]?.requiredForEligible) return null;
    const qualifiedStaff = staffers.filter((staffer) => staffCanTeach(staffer, skill));
    const qualifiedStaffNames = new Set(qualifiedStaff.map((staffer) => staffer.name));
    const allowedRotations = Array.from({ length: ROTATION_COUNT }, (_, rotation) => rotation)
      .filter((rotation) => skillAllowsRotation(skill, rotation, skillsConfig));
    const eligibleRequesters = campers.filter((camper) => {
      if (!camperHasPreference(camper, skill)) return false;
      if (!isSkillAllowedForCamper(skill, camper, skillsConfig)) return false;
      const camperKey = getCamperAssignmentKey(camper);
      if (hasReachedSkillLimit(assignedSkillMap[camperKey], camper, skill, skillsConfig)) return false;
      return allowedRotations.some((rotation) => assignments[camperKey]?.[rotation] === undefined);
    });
    const eligibleDemand = eligibleRequesters.length;
    const eligibleDemandByTribe = {};
    const rankDemand = { 1: 0, 2: 0, 3: 0, '4+': 0 };
    let weightedDemand = 0;
    eligibleRequesters.forEach((camper) => {
      const rank = preferenceRank(camper, skill);
      weightedDemand += getPreferenceDemandWeight(rank);
      eligibleDemandByTribe[camper.tribe] = (eligibleDemandByTribe[camper.tribe] || 0) + 1;
      if (rank <= 3) rankDemand[rank] += 1;
      else rankDemand['4+'] += 1;
    });

    const allRotationOptions = allowedRotations.map((rotation) => ({
      rotation,
      minSize: Math.max(1, getSkillMinSize(skill, groupMin, skillsConfig, rotation)),
      maxSize: Math.max(1, getSkillMaxSize(skill, groupMax, skillsConfig, rotation)),
    }));
    const rotationOptions = allRotationOptions.filter((option) => eligibleRequesters.filter((camper) => (
      assignments[getCamperAssignmentKey(camper)]?.[option.rotation] === undefined
    )).length >= option.minSize);
    const staffedRotationOptions = rotationOptions.filter((option) => {
      const rotationGroups = groups[option.rotation] || {};
      const hasReusableGroup = Object.values(rotationGroups).some((group) => (
        group
        && isGroupEnabled(group)
        && group.staff
        && qualifiedStaffNames.has(group.staff)
        && (group.campers || []).length === 0
        && (
          group.skill === skill
          || !isProtectedProgramShapeGroup(group, option.rotation, settings, skillsConfig)
        )
      ));
      const hasFreeQualifiedStaff = qualifiedStaff.some((staffer) => !Object.values(rotationGroups).some((group) => (
        group && isGroupEnabled(group) && group.staff === staffer.name
      )));
      return hasReusableGroup || hasFreeQualifiedStaff;
    });
    const lowestAllowedMin = allRotationOptions.length > 0
      ? Math.min(...allRotationOptions.map((option) => option.minSize))
      : null;
    const target = lowestAllowedMin != null && eligibleDemand < lowestAllowedMin
      ? {
        targetRuns: 0,
        targetRunReason: 'below-minimum-demand',
        effectiveMin: lowestAllowedMin,
        effectiveMax: Math.max(...allRotationOptions.map((option) => option.maxSize)),
      }
      : computeProgramShapeTarget(eligibleDemand, rotationOptions, staffedRotationOptions);
    return {
      skill,
      eligibleDemand,
      weightedDemand,
      topThreeDemand: (rankDemand[1] || 0) + (rankDemand[2] || 0) + (rankDemand[3] || 0),
      teacherCount: teachersPerSkill[skill] || qualifiedStaff.length,
      allowedRotationCount: rotationOptions.length,
      legalRotationCapacity: rotationOptions.length,
      staffQualifiedRotationCapacity: staffedRotationOptions.length,
      allowedRotations: staffedRotationOptions.map((option) => option.rotation),
      rotationOptions: staffedRotationOptions,
      targetRuns: target.targetRuns,
      committedRuns: 0,
      targetRunReason: target.targetRunReason,
      targetRunShortfall: target.targetRuns,
      effectiveMin: target.effectiveMin,
      effectiveMax: target.effectiveMax,
      preferredTribes: chooseBestTribeWindowForSkill(
        skill,
        { [skill]: eligibleDemandByTribe },
        settings,
        skillsConfig
      ),
    };
  }).filter(Boolean);

  const rotationLoad = Array.from({ length: ROTATION_COUNT }, () => 0);
  groups.forEach((rotationGroups, rotation) => {
    Object.values(rotationGroups || {}).forEach((group) => {
      if (group && isGroupEnabled(group)) rotationLoad[rotation] += 1;
    });
  });

  return plans.sort((a, b) => {
    if ((a.eligibleDemand || 0) !== (b.eligibleDemand || 0)) return (a.eligibleDemand || 0) - (b.eligibleDemand || 0);
    if ((a.teacherCount || 0) !== (b.teacherCount || 0)) return (a.teacherCount || 0) - (b.teacherCount || 0);
    if ((a.allowedRotationCount || 0) !== (b.allowedRotationCount || 0)) return (a.allowedRotationCount || 0) - (b.allowedRotationCount || 0);
    if ((a.topThreeDemand || 0) !== (b.topThreeDemand || 0)) return (b.topThreeDemand || 0) - (a.topThreeDemand || 0);
    return a.skill.localeCompare(b.skill);
  }).map((plan) => ({
    ...plan,
    allowedRotations: (plan.rotationOptions || [])
      .slice()
      .sort((a, b) => a.minSize - b.minSize || rotationLoad[a.rotation] - rotationLoad[b.rotation] || a.rotation - b.rotation)
      .map((option) => option.rotation),
  }));
}

function allocateBalancedTargetSizes(eligibleDemand, selectedRuns) {
  const sizes = selectedRuns.map((run) => run.minSize);
  const maxAssignable = selectedRuns.reduce((sum, run) => sum + run.maxSize, 0);
  let remaining = Math.max(0, Math.min(eligibleDemand, maxAssignable) - sizes.reduce((sum, size) => sum + size, 0));
  while (remaining > 0) {
    const candidates = selectedRuns
      .map((run, idx) => ({ ...run, idx, size: sizes[idx] }))
      .filter((run) => run.size < run.maxSize)
      .sort((a, b) => a.size - b.size || a.rotation - b.rotation || a.gKey.localeCompare(b.gKey));
    if (candidates.length === 0) break;
    sizes[candidates[0].idx] += 1;
    remaining -= 1;
  }
  return sizes;
}

function ensureProgramShapeTargetRunGroups(input, groups, plans, counters = null) {
  const staffers = input.staffers || [];
  const settings = input.settings || {};
  const skillsConfig = input.skillsConfig || {};
  const groupMax = settings.groupMax ?? 25;
  const commitments = [];
  const staffPriority = buildStaffSkillPriority(staffers);

  const findReusableGroup = (skill, rotation) => {
    const rotationGroups = groups[rotation] || {};
    const existingSkillGroup = Object.keys(rotationGroups)
      .sort((a, b) => {
        const groupA = rotationGroups[a];
        const groupB = rotationGroups[b];
        const priorityA = staffPriority[groupA?.staff]?.[skill] ?? 999;
        const priorityB = staffPriority[groupB?.staff]?.[skill] ?? 999;
        return priorityA - priorityB || a.localeCompare(b);
      })
      .find((gKey) => {
        const group = rotationGroups[gKey];
        return group
          && isGroupEnabled(group)
          && group.skill === skill
          && group.staff
          && staffPriority[group.staff]?.[skill] !== undefined
          && (group.campers || []).length === 0;
      });
    if (existingSkillGroup) return existingSkillGroup;

    const qualifiedStaff = staffers
      .filter((staffer) => staffCanTeach(staffer, skill))
      .sort((a, b) => {
        const aRank = (a.skills || []).findIndex((s) => s === skill || (equivalentOneEight(s) && equivalentOneEight(skill)));
        const bRank = (b.skills || []).findIndex((s) => s === skill || (equivalentOneEight(s) && equivalentOneEight(skill)));
        const normA = aRank < 0 ? 999 : aRank;
        const normB = bRank < 0 ? 999 : bRank;
        return normA - normB || (a.name || '').localeCompare(b.name || '');
      });
    for (const staffer of qualifiedStaff) {
      const groupKey = Object.keys(rotationGroups)
        .sort((a, b) => a.localeCompare(b))
        .find((gKey) => {
          const group = rotationGroups[gKey];
          return group
            && isGroupEnabled(group)
            && group.staff === staffer.name
            && (group.campers || []).length === 0
            && !isProtectedProgramShapeGroup(group, rotation, settings, skillsConfig)
            && skillAllowsRotation(skill, rotation, skillsConfig);
        });
      if (groupKey) {
        const group = rotationGroups[groupKey];
        group.skill = skill;
        group.maxSize = getSkillMaxSize(skill, groupMax, skillsConfig, rotation);
        group.preferredTribes = chooseBestTribeWindowForSkill(skill, null, settings, skillsConfig);
        group.enabled = true;
        group.campers = [];
        group.tribes = {};
        if (counters) counters.targetRunGroupsConverted = (counters.targetRunGroupsConverted || 0) + 1;
        return groupKey;
      }
    }
    for (const staffer of qualifiedStaff) {
      const staffBusy = Object.values(rotationGroups).some((group) => (
        group && isGroupEnabled(group) && group.staff === staffer.name
      ));
      if (staffBusy) continue;
      const baseKey = `${skill}__${staffer.name.replace(/\s+/g, '_')}_target_${rotation + 1}`;
      const groupKey = rotationGroups[baseKey]
        ? `${baseKey}_${Object.keys(rotationGroups).length + 1}`
        : baseKey;
      rotationGroups[groupKey] = {
        skill,
        enabled: true,
        maxSize: getSkillMaxSize(skill, groupMax, skillsConfig, rotation),
        allowedGradeMin: null,
        allowedGradeMax: null,
        preferredTribes: chooseBestTribeWindowForSkill(skill, null, settings, skillsConfig),
        campers: [],
        tribes: {},
        staff: staffer.name,
      };
      if (counters) counters.targetRunGroupsCreated = (counters.targetRunGroupsCreated || 0) + 1;
      return groupKey;
    }
    return null;
  };

  const selectedGroupKeys = new Set();
  const selectedRunsBySkill = new Map(plans.map((plan) => [plan.skill, []]));
  const planOrderBySkill = new Map(plans.map((plan, index) => [plan.skill, index]));
  const maxTargetRuns = plans.reduce((max, plan) => Math.max(max, plan.targetRuns || 0), 0);
  const targetAllocationLoad = Array(ROTATION_COUNT).fill(0);

  // Allocate one run per eligible skill before allocating a second run to any
  // skill. This prevents an early plan from monopolizing shared staff/rotations.
  for (let targetRound = 0; targetRound < maxTargetRuns; targetRound += 1) {
    plans.forEach((plan) => {
      if (!plan || plan.targetRuns <= targetRound) return;
      const selectedRuns = selectedRunsBySkill.get(plan.skill) || [];
      const usedRotations = new Set(selectedRuns.map((run) => run.rotation));
      let selected = null;
      const candidateRotations = (plan.allowedRotations || []).slice().sort((a, b) => (
        targetAllocationLoad[a] - targetAllocationLoad[b]
        || a - b
      ));
      for (const rotation of candidateRotations) {
        if (usedRotations.has(rotation)) continue;
        const gKey = findReusableGroup(plan.skill, rotation);
        if (!gKey) continue;
        selected = { rotation, gKey };
        break;
      }
      if (!selected) return;
      const group = groups[selected.rotation][selected.gKey];
      if (!group) return;
      group.preferredTribes = Array.isArray(plan.preferredTribes) ? plan.preferredTribes.slice() : [];
      selectedRuns.push({
        rotation: selected.rotation,
        gKey: selected.gKey,
        targetRound,
        minSize: Math.max(1, getSkillMinSize(plan.skill, settings.groupMin ?? 10, skillsConfig, selected.rotation)),
        maxSize: Math.max(1, getSkillMaxSize(plan.skill, groupMax, skillsConfig, selected.rotation)),
      });
      selectedRunsBySkill.set(plan.skill, selectedRuns);
      selectedGroupKeys.add(`${selected.rotation}|${selected.gKey}`);
      targetAllocationLoad[selected.rotation] += 1;
    });
  }

  plans.forEach((plan) => {
    if (!plan) return;
    const selectedRuns = selectedRunsBySkill.get(plan.skill) || [];
    const targetSizes = allocateBalancedTargetSizes(plan.eligibleDemand, selectedRuns);
    selectedRuns.forEach((run, idx) => {
      commitments.push({
        rotation: run.rotation,
        gKey: run.gKey,
        skill: plan.skill,
        targetSize: targetSizes[idx] || run.minSize,
        minSize: run.minSize,
        eligibleDemand: plan.eligibleDemand,
        programShapeTarget: true,
        targetRound: run.targetRound,
        planOrder: planOrderBySkill.get(plan.skill) || 0,
      });
    });
    plan.committedRuns = selectedRuns.length;
    plan.allocationShortfall = Math.max(0, plan.targetRuns - plan.committedRuns);
    plan.targetRunShortfall = plan.allocationShortfall;
    if (plan.committedRuns < plan.targetRuns && counters) {
      counters.targetRunCommitmentShortfalls = (counters.targetRunCommitmentShortfalls || 0)
        + (plan.targetRuns - plan.committedRuns);
    }
  });

  const plannedSkills = new Set(plans.map((plan) => plan.skill));
  groups.forEach((rotationGroups, rotation) => {
    Object.keys(rotationGroups || {}).forEach((gKey) => {
      const group = rotationGroups[gKey];
      if (!group || !isGroupEnabled(group) || !plannedSkills.has(group.skill)) return;
      if ((group.campers || []).length > 0) return;
      if (selectedGroupKeys.has(`${rotation}|${gKey}`)) return;
      if (isProtectedProgramShapeGroup(group, rotation, settings, skillsConfig)) return;
      disableGroup(group);
    });
  });

  return commitments.sort((a, b) => (
    (a.targetRound || 0) - (b.targetRound || 0)
    || (a.planOrder || 0) - (b.planOrder || 0)
    || a.rotation - b.rotation
    || a.gKey.localeCompare(b.gKey)
  ));
}

function summarizeAssignmentPhase(campers, assignments) {
  const holesByRotation = Array(ROTATION_COUNT).fill(0);
  let filledSlots = 0;
  let unplacedCampers = 0;
  campers.forEach((camper) => {
    const camperAssignments = assignments[getCamperAssignmentKey(camper)] || {};
    let camperMissing = false;
    for (let rotation = 0; rotation < ROTATION_COUNT; rotation += 1) {
      if (camperAssignments[rotation] === undefined) {
        holesByRotation[rotation] += 1;
        camperMissing = true;
      } else {
        filledSlots += 1;
      }
    }
    if (camperMissing) unplacedCampers += 1;
  });
  return {
    filledSlots,
    missingSlots: (campers.length * ROTATION_COUNT) - filledSlots,
    unplacedCampers,
    holesByRotation,
  };
}

function fillCommittedTargetGroups(ctx, commitments) {
  const {
    campers,
    groups,
    assignments,
    assignedSkillMap,
    skillsConfig,
    rand,
    counters,
    groupsBySkill,
    groupMin = 1,
  } = ctx;

  const eligiblePreferenceCountByCamper = new Map(campers.map((camper) => [
    getCamperAssignmentKey(camper),
    (camper.preferences || []).filter((skill) => isSkillAllowedForCamper(skill, camper, skillsConfig)).length,
  ]));

  const states = commitments.map((commitment) => {
    const group = groups[commitment.rotation] && groups[commitment.rotation][commitment.gKey];
    if (!group || !isGroupEnabled(group)) return null;
    return {
      ...commitment,
      group,
      minSize: commitment.minSize
        || getSkillMinSize(commitment.skill, groupMin, skillsConfig, commitment.rotation),
      filledAfterMinimum: (group.campers || []).length,
      filledAfterBalance: (group.campers || []).length,
    };
  }).filter(Boolean);

  let placed = 0;
  const findBestCandidate = (state) => {
    let bestCandidate = null;
    campers.forEach((camper) => {
      const camperKey = getCamperAssignmentKey(camper);
      const camperAssignments = assignments[camperKey] || {};
      if (camperAssignments[state.rotation] !== undefined) return;
      const assignedSet = assignedSkillMap[camperKey] || createAssignedSkillSet();
      if (!canJoinGroup(camper, state.group, assignedSet, state.skill, skillsConfig)) return;

      let remainingPreferenceCount = 0;
      (camper.preferences || []).forEach((alternativeSkill) => {
        if (hasReachedSkillLimit(assignedSet, camper, alternativeSkill, skillsConfig)) return;
        if (!isSkillAllowedForCamper(alternativeSkill, camper, skillsConfig)) return;
        remainingPreferenceCount += 1;
      });
      const openRotations = ROTATION_COUNT - Object.keys(camperAssignments).length;
      const assignedRotations = ROTATION_COUNT - openRotations;
      const eligiblePreferenceCount = eligiblePreferenceCountByCamper.get(camperKey) || 0;
      const rank = preferenceRank(camper, state.skill);
      // Lower is better. Spread coverage across campers first, then protect the
      // campers with the fewest eligible and remaining requested alternatives.
      const score = assignedRotations * 1000000
        + eligiblePreferenceCount * 10000
        + remainingPreferenceCount * 100
        + rank
        + rand();
      if (!bestCandidate || score < bestCandidate.score) {
        bestCandidate = { camper, score };
      }
    });
    return bestCandidate;
  };

  const fillStage = (limitForState, counterName) => {
    let progress = true;
    while (progress) {
      progress = false;
      states.forEach((state) => {
        if ((state.group.campers || []).length >= limitForState(state)) return;
        const bestCandidate = findBestCandidate(state);
        if (!bestCandidate) return;
        assignCamperToGroup(
          bestCandidate.camper,
          state.rotation,
          state.gKey,
          groups,
          assignments,
          assignedSkillMap
        );
        placed += 1;
        progress = true;
        if (counters) {
          counters.commitmentPlacements += 1;
          counters[counterName] = (counters[counterName] || 0) + 1;
        }
      });
    }
  };

  // First give every target run a fair chance to become hard-min viable.
  fillStage((state) => state.minSize, 'targetMinimumPlacements');
  states.forEach((state) => {
    state.filledAfterMinimum = (state.group.campers || []).length;
  });
  const afterMinimum = summarizeAssignmentPhase(campers, assignments);

  // Only after minimum seeding do we balance toward each run's target size.
  fillStage((state) => state.targetSize, 'targetBalancePlacements');
  states.forEach((state) => {
    state.filledAfterBalance = (state.group.campers || []).length;
    if (state.filledAfterBalance < state.targetSize && counters) {
      counters.commitmentShortfalls += state.targetSize - state.filledAfterBalance;
    }
  });
  const afterBalance = summarizeAssignmentPhase(campers, assignments);

  return {
    placed,
    afterMinimum,
    afterBalance,
    groupStats: states.map((state) => ({
      rotation: state.rotation,
      gKey: state.gKey,
      skill: state.skill,
      staff: state.group.staff || null,
      targetRound: state.targetRound || 0,
      minSize: state.minSize,
      targetSize: state.targetSize,
      filledAfterMinimum: state.filledAfterMinimum,
      filledAfterBalance: state.filledAfterBalance,
      commitmentSeatShortfall: Math.max(0, state.targetSize - state.filledAfterBalance),
    })),
  };
}

function attemptSwapRepairs(ctx) {
  const {
    campers,
    groups,
    groupsBySkill,
    assignments,
    assignedSkillMap,
    skillsConfig,
    rand,
    counters,
    demandBySkill,
    demandRankBySkill,
    groupMin,
    protectedGroupKeys,
  } = ctx;

  let changed = false;
  const holes = [];
  campers.forEach((camper) => {
    for (let r = 0; r < ROTATION_COUNT; r++) {
      if ((assignments[getCamperAssignmentKey(camper)] || {})[r] === undefined) {
        holes.push({ camper, rotation: r });
      }
    }
  });
  shuffleInPlace(holes, rand);

  const findAlternativeGroup = (occupant, rotation, excludeGroupKey) => {
    const occupantKey = getCamperAssignmentKey(occupant);
    const occupiedSkills = assignedSkillMap[occupantKey] || new Set();
    const result = [];
    (occupant.preferences || []).forEach((skill) => {
      const keys = (groupsBySkill[rotation] && groupsBySkill[rotation][skill]) || [];
      keys.forEach((gKey) => {
        if (gKey === excludeGroupKey) return;
        const group = groups[rotation][gKey];
        if (!canJoinGroup(occupant, group, occupiedSkills, skill, skillsConfig)) return;
        result.push({ gKey, score: group.campers.length + preferenceRank(occupant, skill) });
      });
    });
    result.sort((a, b) => a.score - b.score);
    return result[0] || null;
  };

  holes.forEach(({ camper, rotation }) => {
    const camperKey = getCamperAssignmentKey(camper);
    if ((assignments[camperKey] || {})[rotation] !== undefined) return;
    const assignedSet = assignedSkillMap[camperKey] || new Set();
    const desiredSkills = (camper.preferences || []).filter((s) => !hasReachedSkillLimit(assignedSet, camper, s, skillsConfig));
    for (const skill of desiredSkills) {
      const keys = ((groupsBySkill[rotation] && groupsBySkill[rotation][skill]) || []).slice();
      shuffleInPlace(keys, rand);
      for (const gKey of keys) {
        const group = groups[rotation][gKey];
        if (!group) continue;
        if (canJoinGroup(camper, group, assignedSet, skill, skillsConfig)) {
          counters.insertionSuccess += 1;
          assignCamperToGroup(camper, rotation, gKey, groups, assignments, assignedSkillMap);
          changed = true;
          return;
        }
        if (group.campers.length < group.maxSize) continue;
        const protectKey = `${rotation}|${gKey}`;
        if (protectedGroupKeys && protectedGroupKeys.has(protectKey) && group.campers.length <= groupMin) {
          continue;
        }
        counters.swapAttempts += 1;
        const occupants = shuffleInPlace(group.campers.slice(), rand);
        for (const occupant of occupants) {
          const alt = findAlternativeGroup(occupant, rotation, gKey);
          if (!alt) continue;
          removeCamperFromGroup(occupant, rotation, gKey, groups, assignments, assignedSkillMap);
          assignCamperToGroup(occupant, rotation, alt.gKey, groups, assignments, assignedSkillMap);
          if (canJoinGroup(camper, group, assignedSet, skill, skillsConfig)) {
            assignCamperToGroup(camper, rotation, gKey, groups, assignments, assignedSkillMap);
            counters.swapSuccess += 1;
            changed = true;
            return;
          }
          // Rollback occupant move if it did not open feasible space for camper.
          removeCamperFromGroup(occupant, rotation, alt.gKey, groups, assignments, assignedSkillMap);
          assignCamperToGroup(occupant, rotation, gKey, groups, assignments, assignedSkillMap);
        }
      }
    }
  });
  return changed;
}

function runDestroyRepair(ctx) {
  const {
    campers,
    groups,
    groupsBySkill,
    assignments,
    assignedSkillMap,
    skillsConfig,
    rand,
    counters,
    demandBySkill,
    demandRankBySkill,
    groupMin,
    protectedGroupKeys,
  } = ctx;

  counters.destroyRepairAttempts += 1;
  const candidates = campers.filter((c) => Object.keys(assignments[getCamperAssignmentKey(c)] || {}).length > 0);
  if (candidates.length === 0) return false;
  shuffleInPlace(candidates, rand);
  const removeCount = Math.max(1, Math.floor(candidates.length * 0.05));
  const selected = candidates.slice(0, removeCount);
  selected.forEach((camper) => {
    const camperKey = getCamperAssignmentKey(camper);
    const assignedRotations = Object.keys(assignments[camperKey] || {}).map((r) => Number(r));
    if (assignedRotations.length === 0) return;
    const removableRotations = assignedRotations.filter((rotation) => {
      const gKey = assignments[camperKey] && assignments[camperKey][rotation];
      if (!gKey) return false;
      const protectKey = `${rotation}|${gKey}`;
      if (!protectedGroupKeys || !protectedGroupKeys.has(protectKey)) return true;
      const group = groups[rotation] && groups[rotation][gKey];
      return !group || (group.campers || []).length > groupMin;
    });
    if (removableRotations.length === 0) return;
    const rotation = randomChoice(removableRotations, rand);
    const gKey = assignments[camperKey][rotation];
    removeCamperFromGroup(camper, rotation, gKey, groups, assignments, assignedSkillMap);
  });
  // Try to refill.
  let changed = false;
  const order = shuffleInPlace(campers.slice(), rand);
  order.forEach((camper) => {
    if (tryAssignRequested(
      camper,
      Array.from({ length: ROTATION_COUNT }, (_, i) => i),
      groups,
      groupsBySkill,
      assignments,
      assignedSkillMap,
      skillsConfig,
      rand,
      demandBySkill,
      demandRankBySkill
    )) {
      changed = true;
    }
  });
  if (attemptSwapRepairs(ctx)) changed = true;
  if (changed) counters.destroyRepairSuccess += 1;
  return changed;
}

function listCandidateGroupsForCamperInRotation(
  camper,
  rotation,
  groups,
  groupsBySkill,
  demandBySkill,
  demandRankBySkill
) {
  const candidates = [];
  (camper.preferences || []).forEach((skill) => {
    const gKeys = (groupsBySkill[rotation] && groupsBySkill[rotation][skill]) || [];
    gKeys.forEach((gKey) => {
      const group = groups[rotation] && groups[rotation][gKey];
      if (!group || !isGroupEnabled(group)) return;
      const rank = preferenceRank(camper, skill);
      const demandRank = demandRankBySkill[skill] || 999;
      const demand = demandBySkill[skill] || 0;
      const fillRatio = group.maxSize > 0 ? (group.campers.length / group.maxSize) : 0;
      const fillPenalty = Math.round((fillRatio ** 2) * 60);
      const score = rank * 100 + demandRank * 3 + demand * 0.02 + group.campers.length + fillPenalty;
      candidates.push({ gKey, skill, score });
    });
  });
  candidates.sort((a, b) => a.score - b.score);
  return candidates;
}

function tryRehomeCamperInRotation(
  camper,
  rotation,
  ctx,
  depth,
  visitedCampers,
  blockedGroupKey = null,
  attemptBudget = null,
  reservedGroupKeys = null
) {
  const {
    groups,
    groupsBySkill,
    assignments,
    assignedSkillMap,
    skillsConfig,
    rand,
    demandBySkill,
    demandRankBySkill,
    counters,
  } = ctx;
  if (attemptBudget && attemptBudget.value <= 0) return false;
  const camperKey = getCamperAssignmentKey(camper);
  const assignedSet = assignedSkillMap[camperKey] || new Set();
  const candidates = listCandidateGroupsForCamperInRotation(
    camper,
    rotation,
    groups,
    groupsBySkill,
    demandBySkill,
    demandRankBySkill
  );

  for (const candidate of candidates) {
    const { gKey, skill } = candidate;
    if (gKey === blockedGroupKey) continue;
    if (reservedGroupKeys && reservedGroupKeys.has(gKey)) continue;
    const group = groups[rotation] && groups[rotation][gKey];
    if (!group || !isGroupEnabled(group)) continue;

    if (canJoinGroup(camper, group, assignedSet, skill, skillsConfig)) {
      assignCamperToGroup(camper, rotation, gKey, groups, assignments, assignedSkillMap);
      return true;
    }

    if (depth <= 0) continue;
    if (!canJoinGroupIgnoringCapacity(camper, group, assignedSet, skill, skillsConfig)) continue;
    if (group.campers.length < group.maxSize) continue;

    const occupants = shuffleInPlace(group.campers.slice(), rand);
    for (const occupant of occupants) {
      if (attemptBudget && attemptBudget.value <= 0) break;
      const occupantKey = getCamperAssignmentKey(occupant);
      if (!occupant || visitedCampers.has(occupantKey)) continue;
      if ((assignments[occupantKey] || {})[rotation] !== gKey) continue;

      if (attemptBudget) attemptBudget.value -= 1;
      counters.finalSwapAttempts += 1;
      removeCamperFromGroup(occupant, rotation, gKey, groups, assignments, assignedSkillMap);
      visitedCampers.add(occupantKey);
      const nextReserved = new Set(reservedGroupKeys || []);
      nextReserved.add(gKey);

      const rehomed = tryRehomeCamperInRotation(
        occupant,
        rotation,
        ctx,
        depth - 1,
        visitedCampers,
        gKey,
        attemptBudget,
        nextReserved
      );
      if (rehomed) {
        assignCamperToGroup(camper, rotation, gKey, groups, assignments, assignedSkillMap);
        return true;
      }

      // Roll back failed branch.
      assignCamperToGroup(occupant, rotation, gKey, groups, assignments, assignedSkillMap);
      visitedCampers.delete(occupantKey);
    }
  }

  return false;
}

function runFinalSwapPhase(
  ctx,
  maxDepth = 2,
  maxPasses = 2,
  maxHolesPerPass = 180,
  maxAttemptsPerPass = 1200
) {
  const {
    campers,
    assignments,
    counters,
  } = ctx;
  let changedAny = false;

  for (let pass = 0; pass < maxPasses; pass++) {
    let changed = false;
    counters.finalSwapPasses += 1;
    const holes = [];
    campers.forEach((camper) => {
      const camAssign = assignments[getCamperAssignmentKey(camper)] || {};
      for (let r = 0; r < ROTATION_COUNT; r++) {
        if (camAssign[r] === undefined) {
          holes.push({ camper, rotation: r });
        }
      }
    });
    if (holes.length === 0) break;

    holes.sort((a, b) => {
      const missingA = ROTATION_COUNT - Object.keys(assignments[getCamperAssignmentKey(a.camper)] || {}).length;
      const missingB = ROTATION_COUNT - Object.keys(assignments[getCamperAssignmentKey(b.camper)] || {}).length;
      if (missingA !== missingB) return missingB - missingA;
      return a.camper.name.localeCompare(b.camper.name);
    });
    const selectedHoles = holes.slice(0, maxHolesPerPass);
    const attemptBudget = { value: maxAttemptsPerPass };

    for (const hole of selectedHoles) {
      if (attemptBudget.value <= 0) break;
      const { camper, rotation } = hole;
      const camperKey = getCamperAssignmentKey(camper);
      if ((assignments[camperKey] || {})[rotation] !== undefined) continue;
      const visitedCampers = new Set([camperKey]);
      const filled = tryRehomeCamperInRotation(
        camper,
        rotation,
        ctx,
        maxDepth,
        visitedCampers,
        null,
        attemptBudget,
        new Set()
      );
      if (filled) {
        counters.finalSwapResolved += 1;
        changed = true;
      }
    }

    changedAny = changedAny || changed;
    if (!changed) break;
  }

  return changedAny;
}

function rebuildAssignmentStateFromSolution(campers, sourceGroups, sourceAssignments, skillsConfig = {}) {
  const groups = cloneGroupLayout(sourceGroups || Array.from({ length: ROTATION_COUNT }, () => ({})));
  const assignments = {};
  const assignedSkillMap = {};
  campers.forEach((camper) => {
    const camperKey = getCamperAssignmentKey(camper);
    assignments[camperKey] = {};
    assignedSkillMap[camperKey] = createAssignedSkillSet();
  });
  campers.forEach((camper) => {
    const camperKey = getCamperAssignmentKey(camper);
    const camAssign = (sourceAssignments && sourceAssignments[camperKey]) || {};
    Object.keys(camAssign).forEach((rStr) => {
      const rotation = Number(rStr);
      const gKey = camAssign[rStr];
      if (!groups[rotation] || !groups[rotation][gKey]) return;
      const skill = groups[rotation][gKey].skill;
      if (hasReachedSkillLimit(assignedSkillMap[camperKey], camper, skill, skillsConfig)) return;
      if ((assignments[camperKey] || {})[rotation] !== undefined) return;
      assignCamperToGroup(camper, rotation, gKey, groups, assignments, assignedSkillMap);
    });
  });
  return { groups, assignments, assignedSkillMap };
}

export function runFinalSwapOnSolution(input, solution, options = {}) {
  const campers = (input && input.campers) || [];
  const settings = (input && input.settings) || {};
  const groupsIn = (solution && solution.groups) || Array.from({ length: ROTATION_COUNT }, () => ({}));
  const assignmentsIn = (solution && solution.assignments) || {};
  const skillsConfig = (input && input.skillsConfig) || {};
  const groupMin = settings.groupMin ?? 10;
  const dynamicMinDelta = settings.dynamicMinDelta ?? DEFAULT_DYNAMIC_MIN_DELTA;
  const rand = createRng(options.seed || 1);
  const precomputed = options.precomputed || null;
  const demandTables = precomputed || computeDemandTables(campers, settings, skillsConfig);
  const demandBySkill = demandTables.demandBySkill;
  const demandRankBySkill = (precomputed && precomputed.demandRankBySkill) || buildDemandRankBySkill(demandBySkill);
  const { groups, assignments, assignedSkillMap } = rebuildAssignmentStateFromSolution(
    campers,
    groupsIn,
    assignmentsIn,
    skillsConfig
  );
  let groupsBySkill = buildGroupIndex(groups);
  const counters = {
    finalSwapPasses: 0,
    finalSwapAttempts: 0,
    finalSwapResolved: 0,
  };

  runFinalSwapPhase(
    {
      campers,
      groups,
      groupsBySkill,
      assignments,
      assignedSkillMap,
      skillsConfig,
      rand,
      counters,
      demandBySkill,
      demandRankBySkill,
    },
    options.maxDepth ?? 3,
    options.maxPasses ?? 3,
    options.maxHolesPerPass ?? 220,
    options.maxAttemptsPerPass ?? 2200
  );

  const repaired = {
    ...solution,
    groups,
    assignments,
    unplaced: gatherUnplacedCampers(campers, assignments),
    preferenceHits: computePreferenceHits(campers, assignments, groups),
    score: computeScore(
      campers,
      assignments,
      groups,
      dynamicMinDelta,
      groupMin,
      demandBySkill,
      precomputed && precomputed.scarcityPenaltySkills,
      {
        weightedDemandBySkill: demandTables.weightedDemandBySkill,
        rankDemandBySkill: demandTables.rankDemandBySkill,
        staffSkillPriority: (precomputed && precomputed.staffSkillPriority) || buildStaffSkillPriority((input && input.staffers) || []),
        skillsConfig,
      }
    ),
  };
  return {
    solution: repaired,
    stageCounters: counters,
    constraintChecks: validateSolutionConstraints(repaired, input),
  };
}

function cloneAssignments(assignments = {}) {
  const copy = {};
  Object.keys(assignments).forEach((camperKey) => {
    copy[camperKey] = { ...(assignments[camperKey] || {}) };
  });
  return copy;
}

export function findCampersWithInsufficientUniquePreferences(campers = [], minimumUniqueSkills = ROTATION_COUNT) {
  return campers
    .map((camper) => {
      const uniqueRequestedSkills = Array.from(new Set((camper.preferences || []).filter(Boolean)));
      return {
        camperId: getCamperAssignmentKey(camper),
        camperName: camper.name,
        uniqueRequestedSkillCount: uniqueRequestedSkills.length,
        uniqueRequestedSkills,
        minimumUniqueSkills,
        shortfall: Math.max(0, minimumUniqueSkills - uniqueRequestedSkills.length),
      };
    })
    .filter((entry) => entry.shortfall > 0)
    .sort((a, b) => (
      b.shortfall - a.shortfall
      || a.camperName.localeCompare(b.camperName)
      || a.camperId.localeCompare(b.camperId)
    ));
}

function cloneDeepRepairGroups(groups = []) {
  return groups.map((rotationGroups) => {
    const copy = {};
    Object.keys(rotationGroups || {}).forEach((gKey) => {
      const group = rotationGroups[gKey];
      copy[gKey] = {
        ...group,
        campers: (group.campers || []).slice(),
        tribes: { ...(group.tribes || {}) },
        allowedGrades: Array.isArray(group.allowedGrades) ? group.allowedGrades.slice() : group.allowedGrades,
        preferredTribes: Array.isArray(group.preferredTribes) ? group.preferredTribes.slice() : group.preferredTribes,
      };
    });
    return copy;
  });
}

function buildDeepRepairCheckpointSolution(input, solution, state, campers, score, staticContext) {
  const checkpoint = {
    ...solution,
    groups: cloneDeepRepairGroups(state.groups),
    assignments: cloneAssignments(state.assignments),
    unplaced: gatherUnplacedCampers(campers, state.assignments),
    preferenceHits: computePreferenceHits(campers, state.assignments, state.groups),
    score,
  };
  checkpoint.demandDiagnostics = buildDemandDiagnostics(input, checkpoint, staticContext);
  return checkpoint;
}

function groupAllowsCamperGrade(group, camper) {
  if (!group || !camper) return false;
  if (Array.isArray(group.allowedGrades) && group.allowedGrades.length > 0) {
    return group.allowedGrades.includes(camper.grade);
  }
  if (Number.isFinite(group.allowedGradeMin) && camper.grade < group.allowedGradeMin) return false;
  if (Number.isFinite(group.allowedGradeMax) && camper.grade > group.allowedGradeMax) return false;
  return true;
}

function isPinnedRequiredAssignment(camper, rotation, group, skillsConfig) {
  if (!group) return false;
  const config = getSkillConfig(group.skill, skillsConfig);
  if (!config.requiredForEligible || !isSkillAllowedForCamper(group.skill, camper, skillsConfig)) return false;
  const allowedRotations = getRequiredAllowedRotations(config);
  return allowedRotations.length === 0 || allowedRotations.includes(rotation);
}

function createDeepRepairDiagnostics(options, initialPhase) {
  return {
    attempted: true,
    cancelled: false,
    budgetExhausted: false,
    budgetMs: Number.isFinite(options.maxRuntimeMs) ? options.maxRuntimeMs : null,
    elapsedMs: 0,
    maxDepthConfigured: options.maxDepth,
    maxDepthAttempted: 0,
    deepestAcceptedPath: 0,
    nodesExpanded: 0,
    holesAtStart: initialPhase.missingSlots,
    holesResolved: 0,
    holesRemaining: initialPhase.missingSlots,
    campersAtStart: initialPhase.unplacedCampers,
    campersFullyResolved: 0,
    passes: 0,
    directInsertions: 0,
    capacityChainsAccepted: 0,
    crossRotationPivotsAccepted: 0,
    minimumBackfillChainsAccepted: 0,
    pathsAttempted: 0,
    pathsAccepted: 0,
    pathsRejected: 0,
    rejectionCounts: {
      'no-requested-run': 0,
      'gender-or-tribe-ineligible': 0,
      'tribe-adjacency': 0,
      'repeat-limit': 0,
      'rotation-disabled': 0,
      'group-unavailable': 0,
      'staff-invalid': 0,
      'required-assignment-pinned': 0,
      'capacity-dead-end': 0,
      'source-at-minimum': 0,
      'minimum-backfill-dead-end': 0,
      cycle: 0,
      'depth-limit': 0,
      'node-budget': 0,
      deadline: 0,
      'constraint-validation': 0,
      'score-not-improved': 0,
    },
    acceptedPaths: [],
    unresolvedHoles: [],
  };
}

function incrementDeepRejection(diagnostics, reason, amount = 1) {
  diagnostics.rejectionCounts[reason] = (diagnostics.rejectionCounts[reason] || 0) + amount;
}

function evaluateDeepRepairDestination(camper, rotation, gKey, state) {
  const group = state.groups[rotation]?.[gKey];
  if (!group || !isGroupEnabled(group)) return { legal: false, reason: 'group-unavailable' };
  if (!camperHasPreference(camper, group.skill)) return { legal: false, reason: 'not-requested' };
  if (!skillAllowsRotation(group.skill, rotation, state.skillsConfig)) {
    return { legal: false, reason: 'rotation-disabled' };
  }
  if (!groupAllowsCamperGrade(group, camper)) return { legal: false, reason: 'gender-or-tribe-ineligible' };
  if (!isSkillAllowedForCamper(group.skill, camper, state.skillsConfig)) {
    return { legal: false, reason: 'gender-or-tribe-ineligible' };
  }
  if (!group.staff || !state.staffByName[group.staff] || !staffCanTeach(state.staffByName[group.staff], group.skill)) {
    return { legal: false, reason: 'staff-invalid' };
  }
  const camperKey = getCamperAssignmentKey(camper);
  const assignedSet = state.assignedSkillMap[camperKey] || createAssignedSkillSet();
  if (hasReachedSkillLimit(assignedSet, camper, group.skill, state.skillsConfig)) {
    return { legal: false, reason: 'repeat-limit' };
  }
  if (!canJoinPreferredTribeWindow(camper, group)) {
    return { legal: false, reason: 'tribe-adjacency' };
  }
  if (!canGroupTribes(getExistingGroupTribes(group), camper.tribe, state.locationTribes)) {
    return { legal: false, reason: 'tribe-adjacency' };
  }
  if ((group.campers || []).length >= group.maxSize) {
    return { legal: false, capacityEligible: true, reason: 'capacity', group };
  }
  return { legal: true, capacityEligible: true, reason: null, group };
}

function applyDeepSearchRemoval(state, camper, rotation, gKey) {
  const group = state.groups[rotation]?.[gKey];
  if (!group) return false;
  const camperKey = getCamperAssignmentKey(camper);
  const index = (group.campers || []).findIndex((entry) => getCamperAssignmentKey(entry) === camperKey);
  if (index < 0 || state.assignments[camperKey]?.[rotation] !== gKey) return false;
  state.moveStack.push({ type: 'remove', camper, rotation, gKey, index });
  removeCamperFromGroup(camper, rotation, gKey, state.groups, state.assignments, state.assignedSkillMap);
  return true;
}

function applyDeepSearchAssignment(state, camper, rotation, gKey) {
  if (state.assignments[getCamperAssignmentKey(camper)]?.[rotation] !== undefined) return false;
  if (!assignCamperToGroup(camper, rotation, gKey, state.groups, state.assignments, state.assignedSkillMap)) return false;
  state.moveStack.push({ type: 'assign', camper, rotation, gKey });
  return true;
}

function rollbackDeepSearch(state, checkpoint) {
  while (state.moveStack.length > checkpoint) {
    const move = state.moveStack.pop();
    if (move.type === 'minimum-backfill') continue;
    if (move.type === 'assign') {
      removeCamperFromGroup(
        move.camper,
        move.rotation,
        move.gKey,
        state.groups,
        state.assignments,
        state.assignedSkillMap
      );
      continue;
    }
    assignCamperToGroup(
      move.camper,
      move.rotation,
      move.gKey,
      state.groups,
      state.assignments,
      state.assignedSkillMap
    );
    const campers = state.groups[move.rotation][move.gKey].campers;
    const restoredIndex = campers.findIndex((entry) => (
      getCamperAssignmentKey(entry) === getCamperAssignmentKey(move.camper)
    ));
    if (restoredIndex >= 0 && restoredIndex !== move.index) {
      const [restored] = campers.splice(restoredIndex, 1);
      campers.splice(Math.min(move.index, campers.length), 0, restored);
    }
  }
}

function buildDeepRepairStateHash(state, camperKey, rotation, blockedGroupKey, depthRemaining) {
  const changedAssignments = [];
  state.pathCamperKeys.forEach((key) => {
    const entries = Object.keys(state.assignments[key] || {})
      .map(Number)
      .sort((a, b) => a - b)
      .map((assignedRotation) => `${assignedRotation}:${state.assignments[key][assignedRotation]}`)
      .join(',');
    changedAssignments.push(`${key}=${entries}`);
  });
  changedAssignments.sort((a, b) => a.localeCompare(b));
  return `${camperKey}|${rotation}|${blockedGroupKey || ''}|${depthRemaining}|${changedAssignments.join(';')}`;
}

function countDeepRepairAlternatives(camper, rotation, state, excludedGroupKey = null) {
  let count = 0;
  (camper.preferences || []).forEach((skill) => {
    const keys = state.groupsBySkill[rotation]?.[skill] || [];
    keys.forEach((gKey) => {
      if (gKey === excludedGroupKey) return;
      const evaluation = evaluateDeepRepairDestination(camper, rotation, gKey, state);
      if (evaluation.legal || evaluation.capacityEligible) count += 1;
    });
  });
  return count;
}

function listDeepRepairCandidates(camper, rotation, state, blockedGroupKey = null, recordRejections = true) {
  const candidates = [];
  const seenGroups = new Set();
  (camper.preferences || []).forEach((skill, preferenceIndex) => {
    const keys = (state.groupsBySkill[rotation]?.[skill] || []).slice().sort((a, b) => a.localeCompare(b));
    keys.forEach((gKey) => {
      if (gKey === blockedGroupKey || seenGroups.has(gKey)) return;
      seenGroups.add(gKey);
      const evaluation = evaluateDeepRepairDestination(camper, rotation, gKey, state);
      if (!evaluation.legal && !evaluation.capacityEligible) {
        if (recordRejections) incrementDeepRejection(state.diagnostics, evaluation.reason);
        return;
      }
      const group = evaluation.group;
      candidates.push({
        rotation,
        gKey,
        skill,
        group,
        direct: evaluation.legal,
        preferenceRank: preferenceIndex + 1,
        openSeats: Math.max(0, group.maxSize - group.campers.length),
      });
    });
  });
  candidates.sort((a, b) => (
    Number(b.direct) - Number(a.direct)
    || a.preferenceRank - b.preferenceRank
    || b.openSeats - a.openSeats
    || a.skill.localeCompare(b.skill)
    || a.gKey.localeCompare(b.gKey)
  ));
  return candidates;
}

function shouldStopDeepRepair(state) {
  if (state.shouldCancel()) {
    state.diagnostics.cancelled = true;
    return true;
  }
  if (state.nodesExpanded >= state.maxNodes || state.nodesExpandedForHole >= state.maxNodesPerHole) {
    state.diagnostics.budgetExhausted = true;
    incrementDeepRejection(state.diagnostics, 'node-budget');
    return true;
  }
  if (Date.now() >= state.deadlineAt) {
    state.diagnostics.budgetExhausted = true;
    incrementDeepRejection(state.diagnostics, 'deadline');
    return true;
  }
  return false;
}

function listDeepRepairOccupants(group, rotation, state, excludedGroupKey) {
  return (group.campers || [])
    .filter((occupant) => !state.pathCamperKeys.has(getCamperAssignmentKey(occupant)))
    .filter((occupant) => !isPinnedRequiredAssignment(occupant, rotation, group, state.skillsConfig))
    .map((occupant) => ({
      occupant,
      alternatives: countDeepRepairAlternatives(occupant, rotation, state, excludedGroupKey),
      currentRank: preferenceRank(occupant, group.skill),
    }))
    .filter((entry) => entry.alternatives > 0)
    .sort((a, b) => (
      b.alternatives - a.alternatives
      || b.currentRank - a.currentRank
      || getCamperAssignmentKey(a.occupant).localeCompare(getCamperAssignmentKey(b.occupant))
    ));
}

function searchDeepRepairIntoGroup(camper, rotation, gKey, state, depthRemaining) {
  if (shouldStopDeepRepair(state) || depthRemaining <= 0) return false;
  const evaluation = evaluateDeepRepairDestination(camper, rotation, gKey, state);
  if (evaluation.legal) return applyDeepSearchAssignment(state, camper, rotation, gKey);
  if (!evaluation.capacityEligible) {
    incrementDeepRejection(state.diagnostics, evaluation.reason);
    return false;
  }

  const group = evaluation.group;
  const occupants = listDeepRepairOccupants(group, rotation, state, gKey);
  for (const { occupant } of occupants) {
    if (shouldStopDeepRepair(state)) return false;
    const checkpoint = state.moveStack.length;
    const occupantKey = getCamperAssignmentKey(occupant);
    if (!applyDeepSearchRemoval(state, occupant, rotation, gKey)) continue;
    const incomingEvaluation = evaluateDeepRepairDestination(camper, rotation, gKey, state);
    if (!incomingEvaluation.legal || !applyDeepSearchAssignment(state, camper, rotation, gKey)) {
      rollbackDeepSearch(state, checkpoint);
      continue;
    }
    state.pathCamperKeys.add(occupantKey);
    if (searchDeepRepairHole(occupant, rotation, state, depthRemaining - 1, gKey)) return true;
    state.pathCamperKeys.delete(occupantKey);
    rollbackDeepSearch(state, checkpoint);
  }
  incrementDeepRejection(state.diagnostics, 'capacity-dead-end');
  return false;
}

function listCrossRotationPivots(camper, targetRotation, state) {
  const camperKey = getCamperAssignmentKey(camper);
  const assignments = state.assignments[camperKey] || {};
  const pivots = [];
  Object.keys(assignments)
    .map(Number)
    .sort((a, b) => a - b)
    .forEach((sourceRotation) => {
      if (sourceRotation === targetRotation) return;
      const sourceGroupKey = assignments[sourceRotation];
      const sourceGroup = state.groups[sourceRotation]?.[sourceGroupKey];
      if (!sourceGroup || !camperHasPreference(camper, sourceGroup.skill)) return;
      if (isPinnedRequiredAssignment(camper, sourceRotation, sourceGroup, state.skillsConfig)) {
        incrementDeepRejection(state.diagnostics, 'required-assignment-pinned');
        return;
      }
      const sourceMin = getSkillMinSize(
        sourceGroup.skill,
        state.groupMin,
        state.skillsConfig,
        sourceRotation
      );
      const sourceSizeAfterRemoval = (sourceGroup.campers || []).length - 1;
      const requiresMinimumBackfill = sourceSizeAfterRemoval > 0 && sourceSizeAfterRemoval < sourceMin;
      if (requiresMinimumBackfill) incrementDeepRejection(state.diagnostics, 'source-at-minimum');
      const targetKeys = (state.groupsBySkill[targetRotation]?.[sourceGroup.skill] || [])
        .slice()
        .sort((a, b) => a.localeCompare(b));
      targetKeys.forEach((targetGroupKey) => {
        const targetGroup = state.groups[targetRotation]?.[targetGroupKey];
        if (!targetGroup || !isGroupEnabled(targetGroup)) return;
        pivots.push({
          sourceRotation,
          sourceGroupKey,
          sourceGroup,
          targetRotation,
          targetGroupKey,
          targetGroup,
          skill: sourceGroup.skill,
          preferenceRank: preferenceRank(camper, sourceGroup.skill),
          targetOpenSeats: Math.max(0, targetGroup.maxSize - targetGroup.campers.length),
          requiresMinimumBackfill,
        });
      });
    });
  return pivots.sort((a, b) => (
    Number(b.targetOpenSeats > 0) - Number(a.targetOpenSeats > 0)
    || a.preferenceRank - b.preferenceRank
    || a.sourceRotation - b.sourceRotation
    || a.targetGroupKey.localeCompare(b.targetGroupKey)
  ));
}

function getDeepRepairGroupMinimum(group, rotation, state) {
  return getSkillMinSize(group.skill, state.groupMin, state.skillsConfig, rotation);
}

function listMinimumBackfillDonors(rotation, deficitGroupKey, state) {
  const deficitGroup = state.groups[rotation]?.[deficitGroupKey];
  if (!deficitGroup) return [];
  const donors = [];
  Object.keys(state.groups[rotation] || {}).sort((a, b) => a.localeCompare(b)).forEach((sourceGroupKey) => {
    if (sourceGroupKey === deficitGroupKey) return;
    const sourceGroup = state.groups[rotation][sourceGroupKey];
    (sourceGroup.campers || []).forEach((donor) => {
      const donorKey = getCamperAssignmentKey(donor);
      if (state.pathCamperKeys.has(donorKey)) return;
      if (isPinnedRequiredAssignment(donor, rotation, sourceGroup, state.skillsConfig)) return;
      const evaluation = evaluateDeepRepairDestination(donor, rotation, deficitGroupKey, state);
      if (!evaluation.legal) return;
      const sourceMin = getDeepRepairGroupMinimum(sourceGroup, rotation, state);
      const sourceSizeAfterRemoval = sourceGroup.campers.length - 1;
      donors.push({
        donor,
        donorKey,
        sourceGroupKey,
        createsDeficit: sourceSizeAfterRemoval > 0 && sourceSizeAfterRemoval < sourceMin,
        alternatives: countDeepRepairAlternatives(donor, rotation, state, deficitGroupKey),
        preferenceRank: preferenceRank(donor, deficitGroup.skill),
      });
    });
  });
  return donors.sort((a, b) => (
    Number(a.createsDeficit) - Number(b.createsDeficit)
    || b.alternatives - a.alternatives
    || a.preferenceRank - b.preferenceRank
    || a.sourceGroupKey.localeCompare(b.sourceGroupKey)
    || a.donorKey.localeCompare(b.donorKey)
  ));
}

// Resolves the root camper's source-rotation hole while carrying at most one
// explicit group-minimum deficit. Each donor closes the current deficit and
// may create the next; acceptance validation guarantees the chain closes.
function searchMinimumBackfillChain(
  rootCamper,
  rotation,
  deficitGroupKey,
  originalSourceGroupKey,
  state,
  depthRemaining
) {
  if (shouldStopDeepRepair(state) || depthRemaining <= 0) return false;
  const deficitGroup = state.groups[rotation]?.[deficitGroupKey];
  if (!deficitGroup) return false;
  const deficitMin = getDeepRepairGroupMinimum(deficitGroup, rotation, state);
  if (deficitGroup.campers.length >= deficitMin) {
    return searchDeepRepairHole(rootCamper, rotation, state, depthRemaining, originalSourceGroupKey);
  }

  const donors = listMinimumBackfillDonors(rotation, deficitGroupKey, state);
  for (const donorEntry of donors) {
    if (shouldStopDeepRepair(state)) return false;
    const checkpoint = state.moveStack.length;
    const { donor, donorKey, sourceGroupKey } = donorEntry;
    state.moveStack.push({ type: 'minimum-backfill' });
    if (!applyDeepSearchRemoval(state, donor, rotation, sourceGroupKey)) {
      rollbackDeepSearch(state, checkpoint);
      continue;
    }
    const donorEvaluation = evaluateDeepRepairDestination(donor, rotation, deficitGroupKey, state);
    if (!donorEvaluation.legal || !applyDeepSearchAssignment(state, donor, rotation, deficitGroupKey)) {
      rollbackDeepSearch(state, checkpoint);
      continue;
    }
    state.pathCamperKeys.add(donorKey);
    const sourceGroup = state.groups[rotation]?.[sourceGroupKey];
    const sourceMin = sourceGroup ? getDeepRepairGroupMinimum(sourceGroup, rotation, state) : Infinity;
    const nextDepth = depthRemaining - 1;
    let resolved = false;
    if (nextDepth > 0
      && sourceGroup
      && sourceGroup.campers.length > 0
      && sourceGroup.campers.length < sourceMin) {
      const rootEvaluation = evaluateDeepRepairDestination(rootCamper, rotation, sourceGroupKey, state);
      if (rootEvaluation.legal) {
        resolved = applyDeepSearchAssignment(state, rootCamper, rotation, sourceGroupKey);
      }
      if (!resolved) {
        resolved = searchMinimumBackfillChain(
          rootCamper,
          rotation,
          sourceGroupKey,
          originalSourceGroupKey,
          state,
          nextDepth
        );
      }
    } else if (nextDepth > 0) {
      resolved = searchDeepRepairHole(rootCamper, rotation, state, nextDepth, originalSourceGroupKey);
    }
    if (resolved) return true;
    state.pathCamperKeys.delete(donorKey);
    rollbackDeepSearch(state, checkpoint);
  }
  incrementDeepRejection(state.diagnostics, 'minimum-backfill-dead-end');
  return false;
}

function searchDeepRepairHole(camper, rotation, state, depthRemaining, blockedGroupKey = null) {
  if (shouldStopDeepRepair(state)) return false;
  if (depthRemaining <= 0) {
    incrementDeepRejection(state.diagnostics, 'depth-limit');
    return false;
  }
  state.nodesExpanded += 1;
  state.nodesExpandedForHole += 1;
  state.diagnostics.nodesExpanded += 1;
  const camperKey = getCamperAssignmentKey(camper);
  const stateHash = buildDeepRepairStateHash(state, camperKey, rotation, blockedGroupKey, depthRemaining);
  const seenDepth = state.transposition.get(stateHash);
  if (seenDepth !== undefined && seenDepth >= depthRemaining) {
    incrementDeepRejection(state.diagnostics, 'cycle');
    return false;
  }
  state.transposition.set(stateHash, depthRemaining);

  const candidates = listDeepRepairCandidates(camper, rotation, state, blockedGroupKey);
  if (candidates.length === 0) incrementDeepRejection(state.diagnostics, 'no-requested-run');
  for (const candidate of candidates) {
    if (shouldStopDeepRepair(state)) return false;
    const checkpoint = state.moveStack.length;
    if (candidate.direct) {
      if (applyDeepSearchAssignment(state, camper, rotation, candidate.gKey)) return true;
      rollbackDeepSearch(state, checkpoint);
      continue;
    }

    const occupants = listDeepRepairOccupants(candidate.group, rotation, state, candidate.gKey);

    for (const { occupant } of occupants) {
      if (shouldStopDeepRepair(state)) return false;
      const occupantKey = getCamperAssignmentKey(occupant);
      const occupantCheckpoint = state.moveStack.length;
      if (!applyDeepSearchRemoval(state, occupant, rotation, candidate.gKey)) continue;
      const incomingEvaluation = evaluateDeepRepairDestination(camper, rotation, candidate.gKey, state);
      if (!incomingEvaluation.legal || !applyDeepSearchAssignment(state, camper, rotation, candidate.gKey)) {
        rollbackDeepSearch(state, occupantCheckpoint);
        continue;
      }
      state.pathCamperKeys.add(occupantKey);
      const rehomed = searchDeepRepairHole(
        occupant,
        rotation,
        state,
        depthRemaining - 1,
        candidate.gKey
      );
      if (rehomed) return true;
      state.pathCamperKeys.delete(occupantKey);
      rollbackDeepSearch(state, occupantCheckpoint);
    }
    incrementDeepRejection(state.diagnostics, 'capacity-dead-end');
    rollbackDeepSearch(state, checkpoint);
  }

  const pivots = listCrossRotationPivots(camper, rotation, state);
  for (const pivot of pivots) {
    if (shouldStopDeepRepair(state)) return false;
    const checkpoint = state.moveStack.length;
    if (!applyDeepSearchRemoval(state, camper, pivot.sourceRotation, pivot.sourceGroupKey)) continue;
    const targetDepth = Math.max(1, depthRemaining - 1);
    if (!searchDeepRepairIntoGroup(camper, rotation, pivot.targetGroupKey, state, targetDepth)) {
      rollbackDeepSearch(state, checkpoint);
      continue;
    }
    const assignmentMovesUsed = state.moveStack
      .slice(checkpoint)
      .filter((move) => move.type === 'assign').length;
    const remainingDepth = depthRemaining - assignmentMovesUsed;
    const sourceResolved = remainingDepth > 0 && (pivot.requiresMinimumBackfill
      ? searchMinimumBackfillChain(
        camper,
        pivot.sourceRotation,
        pivot.sourceGroupKey,
        pivot.sourceGroupKey,
        state,
        remainingDepth
      )
      : searchDeepRepairHole(
        camper,
        pivot.sourceRotation,
        state,
        remainingDepth,
        pivot.sourceGroupKey
      ));
    if (sourceResolved) {
      return true;
    }
    rollbackDeepSearch(state, checkpoint);
  }
  return false;
}

function compareConstraintChecks(candidateChecks, baselineChecks) {
  return Object.keys(candidateChecks || {}).every((key) => (
    (candidateChecks[key] || 0) <= (baselineChecks?.[key] || 0)
  ));
}

function buildDeepRepairMoves(beforeAssignments, afterAssignments, campers, groups) {
  const moves = [];
  campers.forEach((camper) => {
    const camperKey = getCamperAssignmentKey(camper);
    for (let rotation = 0; rotation < ROTATION_COUNT; rotation += 1) {
      const fromGroupKey = beforeAssignments[camperKey]?.[rotation];
      const toGroupKey = afterAssignments[camperKey]?.[rotation];
      if (fromGroupKey === toGroupKey) continue;
      moves.push({
        camperId: camperKey,
        camperName: camper.name,
        fromRotation: fromGroupKey === undefined ? null : rotation,
        fromGroupKey: fromGroupKey ?? null,
        fromSkill: fromGroupKey === undefined ? null : groups[rotation]?.[fromGroupKey]?.skill || null,
        toRotation: toGroupKey === undefined ? null : rotation,
        toGroupKey: toGroupKey ?? null,
        toSkill: toGroupKey === undefined ? null : groups[rotation]?.[toGroupKey]?.skill || null,
        preferenceRank: toGroupKey === undefined ? null : preferenceRank(camper, groups[rotation]?.[toGroupKey]?.skill),
      });
    }
  });
  return moves.sort((a, b) => (
    a.camperId.localeCompare(b.camperId)
    || (a.toRotation ?? a.fromRotation ?? 99) - (b.toRotation ?? b.fromRotation ?? 99)
  ));
}

function pathContainsCrossRotationPivot(moves) {
  const removals = moves.filter((move) => move.fromGroupKey !== null);
  const additions = moves.filter((move) => move.toGroupKey !== null);
  return removals.some((removal) => additions.some((addition) => (
    addition.camperId === removal.camperId
    && addition.toSkill === removal.fromSkill
    && addition.toRotation !== removal.fromRotation
  )));
}

function buildDeepRepairScore(input, assignments, groups, staticContext) {
  const settings = input.settings || {};
  const demandTables = staticContext || computeDemandTables(input.campers || [], settings, input.skillsConfig || {});
  return computeScore(
    input.campers || [],
    assignments,
    groups,
    settings.dynamicMinDelta ?? DEFAULT_DYNAMIC_MIN_DELTA,
    settings.groupMin ?? 10,
    demandTables.demandBySkill || {},
    demandTables.scarcityPenaltySkills,
    {
      weightedDemandBySkill: demandTables.weightedDemandBySkill,
      rankDemandBySkill: demandTables.rankDemandBySkill,
      staffSkillPriority: demandTables.staffSkillPriority || buildStaffSkillPriority(input.staffers || []),
      skillsConfig: input.skillsConfig || {},
    }
  );
}

function listDeepRepairHoles(campers, assignments, state) {
  const holes = [];
  campers.forEach((camper) => {
    const camperKey = getCamperAssignmentKey(camper);
    const missingCount = ROTATION_COUNT - Object.keys(assignments[camperKey] || {}).length;
    for (let rotation = 0; rotation < ROTATION_COUNT; rotation += 1) {
      if (assignments[camperKey]?.[rotation] !== undefined) continue;
      const legalOptions = listDeepRepairCandidates(camper, rotation, state, null, false).length;
      holes.push({
        camper,
        camperKey,
        rotation,
        legalOptions,
        missingCount,
      });
    }
  });
  return holes.sort((a, b) => (
    a.legalOptions - b.legalOptions
    || b.missingCount - a.missingCount
    || a.camperKey.localeCompare(b.camperKey)
    || a.rotation - b.rotation
  ));
}

export function runDeepRequestedRepairOnSolution(input, solution, options = {}) {
  const startedAt = Date.now();
  const unlimitedRuntime = options.maxRuntimeMs === null;
  const maxRuntimeMs = unlimitedRuntime
    ? Number.POSITIVE_INFINITY
    : Math.max(0, options.maxRuntimeMs ?? 5000);
  const maxDepth = Math.max(1, options.maxDepth ?? 8);
  const maxNodes = Math.max(1, options.maxNodes ?? 200000);
  const maxNodesPerHole = Math.max(1, options.maxNodesPerHole ?? 25000);
  const shouldCancel = options.shouldCancel || (() => false);
  const onProgress = typeof options.onProgress === 'function' ? options.onProgress : null;
  const staticContext = options.staticContext || buildStaticOptimizerContext(input);
  const campers = input.campers || [];
  const skillsConfig = input.skillsConfig || {};
  const initialPhase = summarizeAssignmentPhase(campers, solution.assignments || {});
  const maxPasses = Math.max(1, options.maxPasses ?? initialPhase.missingSlots);
  const diagnostics = createDeepRepairDiagnostics({ maxRuntimeMs, maxDepth }, initialPhase);
  if (initialPhase.missingSlots === 0 || maxRuntimeMs === 0) {
    diagnostics.attempted = initialPhase.missingSlots > 0;
    diagnostics.elapsedMs = Date.now() - startedAt;
    return { solution, diagnostics, improved: false, cancelled: false };
  }

  const rebuilt = rebuildAssignmentStateFromSolution(
    campers,
    solution.groups || [],
    solution.assignments || {},
    skillsConfig
  );
  const state = {
    ...rebuilt,
    groupsBySkill: buildGroupIndex(rebuilt.groups),
    skillsConfig,
    locationTribes: getLocationTribes(input.settings || {}),
    staffByName: Object.fromEntries((input.staffers || []).map((staffer) => [staffer.name, staffer])),
    diagnostics,
    moveStack: [],
    pathCamperKeys: new Set(),
    transposition: new Map(),
    shouldCancel,
    deadlineAt: unlimitedRuntime ? Number.POSITIVE_INFINITY : startedAt + maxRuntimeMs,
    maxNodes,
    maxNodesPerHole,
    nodesExpanded: 0,
    nodesExpandedForHole: 0,
    groupMin: (input.settings || {}).groupMin ?? 10,
    onProgress,
  };
  let currentScore = solution.score || buildDeepRepairScore(input, state.assignments, state.groups, staticContext);
  let currentChecks = validateSolutionConstraints({ ...solution, groups: state.groups, assignments: state.assignments }, input);
  let improved = false;

  for (let pass = 0; pass < maxPasses; pass += 1) {
    if (shouldStopDeepRepair(state)) break;
    diagnostics.passes += 1;
    let acceptedInPass = false;
    const holes = listDeepRepairHoles(campers, state.assignments, state);
    if (holes.length === 0) break;

    for (const hole of holes) {
      if (shouldStopDeepRepair(state)) break;
      if (state.assignments[hole.camperKey]?.[hole.rotation] !== undefined) continue;
      state.nodesExpandedForHole = 0;
      let accepted = false;
      for (let depth = 1; depth <= maxDepth; depth += 1) {
        if (shouldStopDeepRepair(state)) break;
        diagnostics.maxDepthAttempted = Math.max(diagnostics.maxDepthAttempted, depth);
        diagnostics.pathsAttempted += 1;
        const checkpoint = state.moveStack.length;
        const assignmentsBefore = cloneAssignments(state.assignments);
        const phaseBefore = summarizeAssignmentPhase(campers, state.assignments);
        state.pathCamperKeys = new Set([hole.camperKey]);
        state.transposition = new Map();
        const found = searchDeepRepairHole(hole.camper, hole.rotation, state, depth);
        if (!found) {
          rollbackDeepSearch(state, checkpoint);
          diagnostics.pathsRejected += 1;
          if (onProgress) {
            onProgress({
              holesAtStart: diagnostics.holesAtStart,
              holesRemaining: summarizeAssignmentPhase(campers, state.assignments).missingSlots,
              campersResolved: diagnostics.campersFullyResolved,
              nodesExpanded: diagnostics.nodesExpanded,
              currentDepth: depth,
              pathsAccepted: diagnostics.pathsAccepted,
              elapsedMs: Date.now() - startedAt,
            });
          }
          continue;
        }

        const candidatePhase = summarizeAssignmentPhase(campers, state.assignments);
        const candidateScore = buildDeepRepairScore(input, state.assignments, state.groups, staticContext);
        const candidateChecks = validateSolutionConstraints(
          { ...solution, groups: state.groups, assignments: state.assignments },
          input
        );
        if (candidatePhase.missingSlots >= phaseBefore.missingSlots
          || !compareConstraintChecks(candidateChecks, currentChecks)) {
          incrementDeepRejection(diagnostics, 'constraint-validation');
          diagnostics.pathsRejected += 1;
          rollbackDeepSearch(state, checkpoint);
          continue;
        }
        if (compareScores(candidateScore, currentScore) >= 0) {
          incrementDeepRejection(diagnostics, 'score-not-improved');
          diagnostics.pathsRejected += 1;
          rollbackDeepSearch(state, checkpoint);
          continue;
        }

        const moves = buildDeepRepairMoves(assignmentsBefore, state.assignments, campers, state.groups);
        const displacedMoves = moves.filter((move) => move.fromGroupKey !== null).length;
        const crossRotationPivot = pathContainsCrossRotationPivot(moves);
        const minimumBackfill = state.moveStack
          .slice(checkpoint)
          .some((move) => move.type === 'minimum-backfill');
        diagnostics.pathsAccepted += 1;
        diagnostics.deepestAcceptedPath = Math.max(diagnostics.deepestAcceptedPath, moves.length);
        diagnostics.holesResolved += phaseBefore.missingSlots - candidatePhase.missingSlots;
        if (minimumBackfill) diagnostics.minimumBackfillChainsAccepted += 1;
        if (crossRotationPivot) diagnostics.crossRotationPivotsAccepted += 1;
        else if (displacedMoves === 0) diagnostics.directInsertions += 1;
        else diagnostics.capacityChainsAccepted += 1;
        diagnostics.acceptedPaths.push({
          rootCamperId: hole.camperKey,
          rootCamperName: hole.camper.name,
          rootRotation: hole.rotation,
          resolutionType: minimumBackfill
            ? 'minimum-backfill-chain'
            : crossRotationPivot
              ? 'cross-rotation-pivot'
            : displacedMoves === 0 ? 'direct' : 'capacity-chain',
          depth: moves.length,
          nodesExpanded: state.nodesExpandedForHole,
          scoreBefore: currentScore,
          scoreAfter: candidateScore,
          moves,
        });
        currentScore = candidateScore;
        currentChecks = candidateChecks;
        state.moveStack = [];
        accepted = true;
        acceptedInPass = true;
        improved = true;
        if (onProgress) {
          const checkpointSolution = buildDeepRepairCheckpointSolution(
            input,
            solution,
            state,
            campers,
            candidateScore,
            staticContext
          );
          onProgress({
            holesAtStart: diagnostics.holesAtStart,
            holesRemaining: candidatePhase.missingSlots,
            campersResolved: diagnostics.campersFullyResolved,
            nodesExpanded: diagnostics.nodesExpanded,
            currentDepth: depth,
            pathsAccepted: diagnostics.pathsAccepted,
            elapsedMs: Date.now() - startedAt,
            checkpointSolution,
            diagnosticsSnapshot: {
              ...diagnostics,
              elapsedMs: Date.now() - startedAt,
              holesRemaining: candidatePhase.missingSlots,
              rejectionCounts: { ...diagnostics.rejectionCounts },
              acceptedPaths: diagnostics.acceptedPaths.slice(),
            },
          });
        }
        break;
      }
      if (accepted) break;
    }
    if (!acceptedInPass) break;
  }

  const finalPhase = summarizeAssignmentPhase(campers, state.assignments);
  const finalUnplaced = gatherUnplacedCampers(campers, state.assignments);
  const fullyResolvedKeys = new Set(
    (solution.unplaced || gatherUnplacedCampers(campers, solution.assignments || {}))
      .filter((camper) => Object.keys(state.assignments[getCamperAssignmentKey(camper)] || {}).length === ROTATION_COUNT)
      .map(getCamperAssignmentKey)
  );
  diagnostics.elapsedMs = Date.now() - startedAt;
  diagnostics.holesRemaining = finalPhase.missingSlots;
  diagnostics.campersFullyResolved = fullyResolvedKeys.size;
  diagnostics.cancelled = diagnostics.cancelled || shouldCancel();
  diagnostics.unresolvedHoles = listDeepRepairHoles(campers, state.assignments, state).map((hole) => ({
    camperId: hole.camperKey,
    camperName: hole.camper.name,
    rotation: hole.rotation,
    requestedSkills: (hole.camper.preferences || []).slice(),
    legalRequestedGroups: hole.legalOptions,
    outcome: diagnostics.budgetExhausted ? 'budget-exhausted' : 'no-path-found',
  }));

  if (!improved) {
    const unchanged = {
      ...solution,
      phaseSnapshots: {
        ...(solution.phaseSnapshots || {}),
        beforeDeepRepair: initialPhase,
        afterDeepRepair: finalPhase,
      },
    };
    unchanged.demandDiagnostics = buildDemandDiagnostics(input, unchanged, staticContext);
    return {
      solution: unchanged,
      diagnostics,
      improved: false,
      cancelled: diagnostics.cancelled,
    };
  }

  const repaired = {
    ...solution,
    groups: state.groups,
    assignments: state.assignments,
    unplaced: finalUnplaced,
    preferenceHits: computePreferenceHits(campers, state.assignments, state.groups),
    score: currentScore,
    phaseSnapshots: {
      ...(solution.phaseSnapshots || {}),
      beforeDeepRepair: initialPhase,
      afterDeepRepair: finalPhase,
    },
  };
  repaired.demandDiagnostics = buildDemandDiagnostics(input, repaired, staticContext);
  return {
    solution: repaired,
    diagnostics,
    improved,
    cancelled: diagnostics.cancelled,
  };
}

function computePreferenceHits(campers, assignments, groups) {
  const hits = { none: 0 };
  for (let i = 1; i <= 10; i++) hits[i] = 0;
  campers.forEach((camper) => {
    const camperKey = getCamperAssignmentKey(camper);
    const seen = new Set();
    Object.keys(assignments[camperKey] || {}).forEach((rStr) => {
      const r = Number(rStr);
      const gKey = assignments[camperKey][r];
      const group = groups[r] && groups[r][gKey];
      if (!group) return;
      const skill = group.skill;
      if (seen.has(skill)) return;
      seen.add(skill);
      const rank = preferenceRank(camper, skill);
      if (rank === 11) hits.none += 1;
      else hits[rank] = (hits[rank] || 0) + 1;
    });
  });
  return hits;
}

function computeScore(
  campers,
  assignments,
  groups,
  dynamicMinDelta,
  groupMin,
  demandBySkill,
  scarcityPenaltySkills = null,
  options = {}
) {
  let unplacedCampers = 0;
  let missingSlots = 0;
  let preferencePenalty = 0;
  let weightedPreferencePenalty = 0;
  let zeroPreferenceCampers = 0;
  let topOneMissCampers = 0;
  let topThreeMissCampers = 0;
  campers.forEach((camper) => {
    const camAssign = assignments[getCamperAssignmentKey(camper)] || {};
    const count = Object.keys(camAssign).length;
    if (count < ROTATION_COUNT) unplacedCampers += 1;
    missingSlots += (ROTATION_COUNT - count);
    const topOne = (camper.preferences || [])[0];
    const topThree = new Set((camper.preferences || []).slice(0, 3));
    let requestedHitCount = 0;
    let topOneHit = false;
    let topThreeHit = false;
    Object.keys(camAssign).forEach((rStr) => {
      const r = Number(rStr);
      const gKey = camAssign[r];
      const group = groups[r] && groups[r][gKey];
      if (!group) {
        preferencePenalty += 15;
        weightedPreferencePenalty += 50;
        return;
      }
      const rank = preferenceRank(camper, group.skill);
      preferencePenalty += rank;
      weightedPreferencePenalty += rank === 11 ? 50 : Math.max(0, rank - 1) * getPreferenceDemandWeight(rank);
      if (camperHasPreference(camper, group.skill)) {
        requestedHitCount += 1;
        if (group.skill === topOne) topOneHit = true;
        if (topThree.has(group.skill)) topThreeHit = true;
      }
    });
    if (requestedHitCount === 0) zeroPreferenceCampers += 1;
    if (topOne && !topOneHit) topOneMissCampers += 1;
    if (topThree.size > 0 && !topThreeHit) topThreeMissCampers += 1;
  });

  let groupBalancePenalty = 0;
  let skillDistributionPenalty = 0;
  let saturationPenalty = 0;
  let emptyEnabledGroupPenalty = 0;
  let dynamicMinPenalty = 0;
  let hardMinViolations = 0;
  let hardMinCampers = 0;
  let skillCapacityShortfall = 0;
  let saturatedHighDemandRuns = 0;
  let underfilledLowDemandRuns = 0;
  let staffFallbackPenalty = 0;
  const skillSizes = {};
  const plannedCapacityBySkill = {};
  const weightedDemandBySkill = options.weightedDemandBySkill || {};
  const rankDemandBySkill = options.rankDemandBySkill || {};
  const staffSkillPriority = options.staffSkillPriority || {};
  const skillsConfig = options.skillsConfig || {};
  let requiredPreferenceShortfall = 0;
  campers.forEach((camper) => {
    const camAssign = assignments[getCamperAssignmentKey(camper)] || {};
    Object.keys(skillsConfig || {}).forEach((skill) => {
      const config = skillsConfig[skill];
      if (!config?.requiredForEligible || !isSkillAllowedForCamper(skill, camper, skillsConfig)) return;
      const assignedRotations = Object.keys(camAssign)
        .map(Number)
        .filter((rotation) => groups[rotation]?.[camAssign[rotation]]?.skill === skill);
      if (!requiredScheduleIsSatisfied(config, assignedRotations)) requiredPreferenceShortfall += 1;
    });
  });
  groups.forEach((rotationGroups, rotation) => {
    const sizes = Object.keys(rotationGroups)
      .map((gKey) => rotationGroups[gKey])
      .filter((group) => isGroupEnabled(group))
      .map((group) => {
        const size = group.campers.length;
        const maxSize = Math.max(1, group.maxSize || 25);
        plannedCapacityBySkill[group.skill] = (plannedCapacityBySkill[group.skill] || 0) + maxSize;
        if (group.staff && staffSkillPriority[group.staff]?.[group.skill] > 0 && size > 0) {
          staffFallbackPenalty += staffSkillPriority[group.staff][group.skill] * Math.max(1, size);
        }
        if (size === 0) {
          emptyEnabledGroupPenalty += 1;
        } else {
          const fillRatio = size / maxSize;
          if (fillRatio >= 1) saturationPenalty += 6;
          else if (fillRatio >= 0.95) saturationPenalty += 3;
          else if (fillRatio >= 0.9) saturationPenalty += 1;
          const weightedDemand = weightedDemandBySkill[group.skill] || ((demandBySkill?.[group.skill] || 0) * getPreferenceDemandWeight(3));
          const highDemand = weightedDemand >= maxSize * getPreferenceDemandWeight(2);
          const skillMin = getSkillMinSize(group.skill, groupMin, skillsConfig, rotation);
          const lowDemand = (demandBySkill?.[group.skill] || 0) <= Math.max(skillMin, maxSize * 0.5);
          if (highDemand && size >= maxSize) saturatedHighDemandRuns += 1;
          if (lowDemand && size > 0 && size < skillMin) underfilledLowDemandRuns += 1;
          if (size < skillMin) {
            hardMinViolations += 1;
            hardMinCampers += size;
          }
          if (!skillSizes[group.skill]) skillSizes[group.skill] = [];
          skillSizes[group.skill].push(size);
        }
        return size;
      })
      .filter((size) => size > 0);
    if (sizes.length === 0) return;
    const avg = sizes.reduce((a, b) => a + b, 0) / sizes.length;
    const variance = sizes.reduce((sum, size) => sum + ((size - avg) ** 2), 0) / sizes.length;
    groupBalancePenalty += Math.round(variance * 10);
    const effectiveMin = Math.max(0, Math.floor(avg - dynamicMinDelta));
    sizes.forEach((size) => {
      if (size < effectiveMin) dynamicMinPenalty += (effectiveMin - size);
    });
  });

  Object.keys(weightedDemandBySkill || {}).forEach((skill) => {
    const weightedDemand = weightedDemandBySkill[skill] || 0;
    const weightedCapacity = (plannedCapacityBySkill[skill] || 0) * getPreferenceDemandWeight(1);
    const topThreeDemand = (rankDemandBySkill[skill]?.[1] || 0)
      + (rankDemandBySkill[skill]?.[2] || 0)
      + (rankDemandBySkill[skill]?.[3] || 0);
    if (topThreeDemand > 0 && weightedDemand > weightedCapacity) {
      skillCapacityShortfall += Math.ceil((weightedDemand - weightedCapacity) / getPreferenceDemandWeight(1));
    }
  });

  Object.keys(skillSizes).forEach((skill) => {
    const sizes = skillSizes[skill];
    if (!sizes || sizes.length <= 1) return;
    const avg = sizes.reduce((a, b) => a + b, 0) / sizes.length;
    const variance = sizes.reduce((sum, size) => sum + ((size - avg) ** 2), 0) / sizes.length;
    skillDistributionPenalty += Math.round(variance * 10);
  });

  const scarcitySkills = scarcityPenaltySkills || new Set(
    Object.keys(demandBySkill || {})
      .sort((a, b) => (demandBySkill[a] || 0) - (demandBySkill[b] || 0) || a.localeCompare(b))
      .slice(0, Math.max(1, Math.floor(Object.keys(demandBySkill || {}).length / 3)))
  );
  let scarcityMissPenalty = 0;
  campers.forEach((camper) => {
    const requestedScarce = (camper.preferences || []).filter((skill) => scarcitySkills.has(skill));
    if (requestedScarce.length === 0) return;
    const assigned = assignments[getCamperAssignmentKey(camper)] || {};
    const assignedSkills = new Set();
    Object.keys(assigned).forEach((rStr) => {
      const r = Number(rStr);
      const gKey = assigned[r];
      const group = groups[r] && groups[r][gKey];
      if (!group || !isGroupEnabled(group)) return;
      assignedSkills.add(group.skill);
    });
    const gotOne = requestedScarce.some((skill) => assignedSkills.has(skill));
    if (!gotOne) scarcityMissPenalty += 1;
  });

  return {
    hardMinViolations,
    hardMinCampers,
    unplacedCampers,
    missingSlots,
    zeroPreferenceCampers,
    topOneMissCampers,
    topThreeMissCampers,
    requiredPreferenceShortfall,
    weightedPreferencePenalty,
    skillCapacityShortfall,
    saturatedHighDemandRuns,
    underfilledLowDemandRuns,
    staffFallbackPenalty,
    scarcityMissPenalty,
    groupBalancePenalty,
    skillDistributionPenalty,
    saturationPenalty,
    emptyEnabledGroupPenalty,
    preferencePenalty,
    dynamicMinPenalty,
  };
}

export function compareScores(a, b) {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const keys = [
    'hardConstraintViolations',
    'hardMinViolations',
    'hardMinCampers',
    'unplacedCampers',
    'missingSlots',
    'zeroPreferenceCampers',
    'topThreeMissCampers',
    'requiredPreferenceShortfall',
    'weightedPreferencePenalty',
    'preferencePenalty',
    'skillCapacityShortfall',
    'saturatedHighDemandRuns',
    'underfilledLowDemandRuns',
    'staffFallbackPenalty',
    'groupBalancePenalty',
    'skillDistributionPenalty',
    'saturationPenalty',
    'emptyEnabledGroupPenalty',
    'dynamicMinPenalty',
    'scarcityMissPenalty',
  ];
  for (const key of keys) {
    if ((a[key] || 0) < (b[key] || 0)) return -1;
    if ((a[key] || 0) > (b[key] || 0)) return 1;
  }
  return 0;
}

function gatherUnplacedCampers(campers, assignments) {
  return campers.filter((camper) => Object.keys(assignments[getCamperAssignmentKey(camper)] || {}).length < ROTATION_COUNT);
}

function getRequiredPatternCandidates(config) {
  const patterns = getRequiredSchedulePatterns(config);
  if (patterns.length > 0) return patterns;
  return Array.from({ length: ROTATION_COUNT }, (_, rotation) => ({
    rotations: [rotation],
    positions: [rotation],
  }));
}

function getRequiredRotationCapacity(skill, rotation, groups, staffers, skillsConfig, groupMax) {
  const rotationGroups = groups[rotation] || {};
  let capacity = 0;
  const countedStaff = new Set();
  Object.values(rotationGroups).forEach((group) => {
    if (!group || !isGroupEnabled(group) || group.skill !== skill || !group.staff) return;
    capacity += Math.max(0, Math.max(1, group.maxSize || getSkillMaxSize(skill, groupMax, skillsConfig, rotation)) - (group.campers || []).length);
    countedStaff.add(group.staff);
  });
  (staffers || [])
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    .forEach((staffer) => {
      if (!staffer?.name || countedStaff.has(staffer.name) || !staffCanTeach(staffer, skill)) return;
      const occupiedGroup = Object.values(rotationGroups).find((group) => (
        group && isGroupEnabled(group) && group.staff === staffer.name
      ));
      if (occupiedGroup && (occupiedGroup.campers || []).length > 0) return;
      if (occupiedGroup && skillsConfig[occupiedGroup.skill]?.requiredForEligible) return;
      capacity += getSkillMaxSize(skill, groupMax, skillsConfig, rotation);
      countedStaff.add(staffer.name);
    });
  return capacity;
}

function ensureRequiredGroupsForRotation({
  skill,
  rotation,
  requiredSeats,
  groups,
  staffers,
  skillsConfig,
  settings,
  counters,
}) {
  if (!groups[rotation]) groups[rotation] = {};
  const rotationGroups = groups[rotation];
  const groupMax = settings.groupMax ?? 25;
  const effectiveMin = getSkillMinSize(skill, settings.groupMin ?? 10, skillsConfig, rotation);
  const effectiveMax = getSkillMaxSize(skill, groupMax, skillsConfig, rotation);
  const existingRequiredSeats = Object.values(rotationGroups).reduce((sum, group) => (
    group && isGroupEnabled(group) && group.skill === skill ? sum + (group.campers || []).length : sum
  ), 0);
  const totalRequiredSeats = existingRequiredSeats + requiredSeats;
  const minimumGroupsNeeded = Math.ceil(totalRequiredSeats / Math.max(1, effectiveMax));
  if (totalRequiredSeats > 0 && (
    totalRequiredSeats < effectiveMin
    || Math.floor(totalRequiredSeats / Math.max(1, minimumGroupsNeeded)) < effectiveMin
  )) {
    return [];
  }
  const existing = Object.keys(rotationGroups)
    .filter((key) => {
      const group = rotationGroups[key];
      return group && isGroupEnabled(group) && group.skill === skill && group.staff;
    })
    .sort((a, b) => a.localeCompare(b));
  const selected = [];
  let selectedCapacity = 0;
  existing.forEach((key) => {
    if (selectedCapacity >= requiredSeats) return;
    selected.push(key);
    selectedCapacity += Math.max(
      0,
      Math.max(1, rotationGroups[key].maxSize || getSkillMaxSize(skill, groupMax, skillsConfig, rotation))
        - (rotationGroups[key].campers || []).length
    );
  });

  const occupiedStaff = new Set(Object.values(rotationGroups)
    .filter((group) => group && isGroupEnabled(group) && group.staff)
    .map((group) => group.staff));
  const candidates = (staffers || [])
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    .filter((staffer) => staffer?.name && staffCanTeach(staffer, skill))
    .filter((staffer) => !selected.some((key) => rotationGroups[key]?.staff === staffer.name));

  for (const staffer of candidates) {
    if (selectedCapacity >= requiredSeats) break;
    const occupiedKey = Object.keys(rotationGroups).find((key) => {
      const group = rotationGroups[key];
      return group && isGroupEnabled(group) && group.staff === staffer.name;
    });
    if (occupiedKey) {
      const occupiedGroup = rotationGroups[occupiedKey];
      if ((occupiedGroup.campers || []).length > 0) continue;
      if (skillsConfig[occupiedGroup.skill]?.requiredForEligible) continue;
      delete rotationGroups[occupiedKey];
      occupiedStaff.delete(staffer.name);
    }
    if (occupiedStaff.has(staffer.name)) continue;
    const baseKey = `${skill}__${staffer.name.replace(/\s+/g, '_')}_required_${rotation + 1}`;
    let groupKey = baseKey;
    let suffix = 2;
    while (rotationGroups[groupKey]) {
      groupKey = `${baseKey}_${suffix}`;
      suffix += 1;
    }
    rotationGroups[groupKey] = {
      skill,
      enabled: true,
      maxSize: getSkillMaxSize(skill, groupMax, skillsConfig, rotation),
      allowedGradeMin: null,
      allowedGradeMax: null,
      preferredTribes: [],
      campers: [],
      tribes: {},
      staff: staffer.name,
    };
    selected.push(groupKey);
    selectedCapacity += rotationGroups[groupKey].maxSize;
    occupiedStaff.add(staffer.name);
    if (counters) counters.requiredGroupsCreated = (counters.requiredGroupsCreated || 0) + 1;
  }
  return selected;
}

function ensureRequiredSchedules(ctx) {
  const {
    campers,
    staffers,
    groups,
    assignments,
    assignedSkillMap,
    skillsConfig,
    settings,
    counters,
  } = ctx;
  let changed = false;
  const groupMax = settings.groupMax ?? 25;
  const requiredSkills = Object.keys(skillsConfig || {})
    .filter((skill) => skillsConfig[skill]?.requiredForEligible)
    .sort((a, b) => {
      const preferredA = getRequiredSchedule(skillsConfig[a]).preferredPosition;
      const preferredB = getRequiredSchedule(skillsConfig[b]).preferredPosition;
      return preferredA - preferredB || a.localeCompare(b);
    });
  requiredSkills.forEach((skill) => {
    const config = skillsConfig[skill];
    const patterns = getRequiredPatternCandidates(config);
    const plannedByRotation = new Map();
    const eligibleCampers = campers
      .filter((camper) => isSkillAllowedForCamper(skill, camper, skillsConfig))
      .slice()
      .sort((a, b) => getCamperAssignmentKey(a).localeCompare(getCamperAssignmentKey(b)));

    eligibleCampers.forEach((camper) => {
      const camperKey = getCamperAssignmentKey(camper);
      const assignedRequiredRotations = Object.keys(assignments[camperKey] || {})
        .map(Number)
        .filter((rotation) => {
          const groupKey = assignments[camperKey]?.[rotation];
          return groups[rotation]?.[groupKey]?.skill === skill;
        });
      if (requiredScheduleIsSatisfied(config, assignedRequiredRotations)) return;
      const pattern = patterns.find((candidate) => candidate.rotations.every((rotation) => {
        const currentGroupKey = assignments[camperKey]?.[rotation];
        const currentGroup = currentGroupKey ? groups[rotation]?.[currentGroupKey] : null;
        if (currentGroup && currentGroup.skill !== skill && skillsConfig[currentGroup.skill]?.requiredForEligible) {
          return false;
        }
        const alreadyAssigned = currentGroup?.skill === skill;
        const plannedSeats = plannedByRotation.get(rotation)?.length || 0;
        const capacity = getRequiredRotationCapacity(skill, rotation, groups, staffers, skillsConfig, groupMax);
        return alreadyAssigned || plannedSeats + 1 <= capacity;
      }));
      if (!pattern) return;
      pattern.rotations.forEach((rotation) => {
        const currentGroupKey = assignments[camperKey]?.[rotation];
        if (currentGroupKey && groups[rotation]?.[currentGroupKey]?.skill === skill) return;
        if (!plannedByRotation.has(rotation)) plannedByRotation.set(rotation, []);
        plannedByRotation.get(rotation).push(camper);
      });
    });

    Array.from(plannedByRotation.keys()).sort((a, b) => a - b).forEach((rotation) => {
      const plannedCampers = plannedByRotation.get(rotation);
      const groupKeys = ensureRequiredGroupsForRotation({
        skill,
        rotation,
        requiredSeats: plannedCampers.length,
        groups,
        staffers,
        skillsConfig,
        settings,
        counters,
      });
      plannedCampers.forEach((camper) => {
          const camperKey = getCamperAssignmentKey(camper);
          const currentGroupKey = assignments[camperKey]?.[rotation];
          const currentGroup = currentGroupKey ? groups[rotation]?.[currentGroupKey] : null;
          if (currentGroup && currentGroup.skill === skill) return;
          const sortedGroups = groupKeys
            .filter((groupKey) => groups[rotation]?.[groupKey])
            .sort((a, b) => (
              groups[rotation][a].campers.length - groups[rotation][b].campers.length
              || a.localeCompare(b)
            ));
          const assignedSet = assignedSkillMap[camperKey] || createAssignedSkillSet();
          const targetGroupKey = sortedGroups.find((groupKey) => canJoinGroup(
            camper,
            groups[rotation][groupKey],
            assignedSet,
            skill,
            skillsConfig,
            { ignorePreference: true, locationTribes: getLocationTribes(settings) }
          ));
          if (!targetGroupKey) return;
          if (currentGroupKey && currentGroup) {
            removeCamperFromGroup(camper, rotation, currentGroupKey, groups, assignments, assignedSkillMap);
          }
          assignCamperToGroup(camper, rotation, targetGroupKey, groups, assignments, assignedSkillMap);
          changed = true;
          if (counters) counters.requiredPlacements = (counters.requiredPlacements || 0) + 1;
      });
    });
  });
  return changed;
}

function removeInvalidRepeatedAssignments(
  campers,
  groups,
  assignments,
  assignedSkillMap,
  skillsConfig,
  counters = null
) {
  let removed = 0;
  campers.forEach((camper) => {
    const camperKey = getCamperAssignmentKey(camper);
    const skillCounts = Object.create(null);
    Object.keys(assignments[camperKey] || {})
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((rotation) => {
        const gKey = assignments[camperKey]?.[rotation];
        const group = groups[rotation]?.[gKey];
        if (!group || !isGroupEnabled(group)) return;
        const limit = getSkillRepeatLimit(group.skill, camper, skillsConfig);
        if ((skillCounts[group.skill] || 0) >= limit) {
          removeCamperFromGroup(camper, rotation, gKey, groups, assignments, assignedSkillMap);
          removed += 1;
          return;
        }
        skillCounts[group.skill] = (skillCounts[group.skill] || 0) + 1;
      });
  });
  if (counters && removed > 0) {
    counters.invalidRepeatAssignmentsRemoved = (counters.invalidRepeatAssignmentsRemoved || 0) + removed;
  }
  return removed;
}

function copyAssignmentStateIntoTarget(targetGroups, targetAssignments, targetAssignedSkillMap, sourceState) {
  targetGroups.splice(0, targetGroups.length, ...sourceState.groups);
  Object.keys(targetAssignments).forEach((key) => delete targetAssignments[key]);
  Object.keys(sourceState.assignments).forEach((key) => {
    targetAssignments[key] = sourceState.assignments[key];
  });
  Object.keys(targetAssignedSkillMap).forEach((key) => delete targetAssignedSkillMap[key]);
  Object.keys(sourceState.assignedSkillMap).forEach((key) => {
    targetAssignedSkillMap[key] = sourceState.assignedSkillMap[key];
  });
}

function hasBlockingConstraintViolations(checks, input = {}) {
  const skillsConfig = input.skillsConfig || {};
  const staffers = input.staffers || [];
  const oneEightActive = !!skillsConfig[ONE_EIGHT] || staffers.some((staffer) => staffCanTeach(staffer, ONE_EIGHT));
  return Object.keys(checks || {}).some((key) => {
    if (key === 'oneEightRunViolations' && !oneEightActive) return false;
    return (checks[key] || 0) > 0;
  });
}

function chooseBestTribeWindowForSkill(skill, demandBySkillTribe, settings, skillsConfig) {
  const windows = getSkillEligibleTribeWindows(skill, settings, skillsConfig);
  if (windows.length === 0) return [];
  return windows
    .slice()
    .sort((a, b) => {
      const demandA = a.reduce((sum, tribe) => sum + (demandBySkillTribe?.[skill]?.[tribe] || 0), 0);
      const demandB = b.reduce((sum, tribe) => sum + (demandBySkillTribe?.[skill]?.[tribe] || 0), 0);
      return demandB - demandA || getWindowKey(a).localeCompare(getWindowKey(b));
    })[0];
}

function attemptLayoutMutationRepair(ctx) {
  const {
    campers,
    groups,
    assignments,
    assignedSkillMap,
    skillsConfig,
    settings,
    rand,
    counters,
    demandBySkill,
    demandBySkillTribe,
    weightedDemandBySkill,
    rankDemandBySkill,
    groupMin,
    dynamicMinDelta,
    staffers,
    scarcityPenaltySkills,
    staffSkillPriority,
  } = ctx;

  const skillStats = {};
  groups.forEach((rotationGroups) => {
    Object.keys(rotationGroups || {}).forEach((gKey) => {
      const group = rotationGroups[gKey];
      if (!group || !isGroupEnabled(group)) return;
      if (!skillStats[group.skill]) skillStats[group.skill] = { capacity: 0, filled: 0 };
      skillStats[group.skill].capacity += Math.max(1, group.maxSize || (settings.groupMax ?? 25));
      skillStats[group.skill].filled += (group.campers || []).length;
    });
  });

  const staffByName = {};
  (staffers || []).forEach((staffer) => {
    if (staffer?.name) staffByName[staffer.name] = staffer;
  });

  const candidates = [];
  groups.forEach((rotationGroups, rotation) => {
    Object.keys(rotationGroups || {}).forEach((gKey) => {
      const group = rotationGroups[gKey];
      if (!group || !isGroupEnabled(group) || !group.staff) return;
      if (skillsConfig[group.skill]?.requiredForEligible) return;
      const size = (group.campers || []).length;
      const minSize = getSkillMinSize(group.skill, groupMin, skillsConfig, rotation);
      if (size >= minSize) return;
      const currentDemand = demandBySkill[group.skill] || 0;
      candidates.push({ rotation, gKey, group, size, currentDemand });
    });
  });
  candidates.sort((a, b) => a.size - b.size || a.currentDemand - b.currentDemand || a.rotation - b.rotation || a.gKey.localeCompare(b.gKey));

  for (const candidate of candidates.slice(0, 12)) {
    const staffer = staffByName[candidate.group.staff];
    if (!staffer) continue;
    const targetSkills = (staffer.skills || [])
      .filter((skill, idx, arr) => arr.indexOf(skill) === idx)
      .filter((skill) => skill !== candidate.group.skill)
      .filter((skill) => !skillsConfig[skill]?.requiredForEligible)
      .filter((skill) => skillAllowsRotation(skill, candidate.rotation, skillsConfig))
      .filter((skill) => (demandBySkill[skill] || 0) > (skillStats[skill]?.filled || 0))
      .sort((a, b) => {
        const unmetA = Math.max(0, (weightedDemandBySkill?.[a] || 0) - ((skillStats[a]?.capacity || 0) * getPreferenceDemandWeight(1)));
        const unmetB = Math.max(0, (weightedDemandBySkill?.[b] || 0) - ((skillStats[b]?.capacity || 0) * getPreferenceDemandWeight(1)));
        return unmetB - unmetA
          || (weightedDemandBySkill?.[b] || 0) - (weightedDemandBySkill?.[a] || 0)
          || a.localeCompare(b);
      });

    for (const targetSkill of targetSkills.slice(0, 4)) {
      const currentScore = computeScore(
        campers,
        assignments,
        groups,
        dynamicMinDelta,
        groupMin,
        demandBySkill,
        scarcityPenaltySkills,
        { weightedDemandBySkill, rankDemandBySkill, staffSkillPriority, skillsConfig }
      );
      const trial = rebuildAssignmentStateFromSolution(campers, groups, assignments, skillsConfig);
      const trialGroup = trial.groups[candidate.rotation] && trial.groups[candidate.rotation][candidate.gKey];
      if (!trialGroup) continue;
      (trialGroup.campers || []).slice().forEach((camper) => {
        removeCamperFromGroup(camper, candidate.rotation, candidate.gKey, trial.groups, trial.assignments, trial.assignedSkillMap);
      });
      trialGroup.skill = targetSkill;
      trialGroup.maxSize = getSkillMaxSize(targetSkill, settings.groupMax ?? 25, skillsConfig, candidate.rotation);
      trialGroup.preferredTribes = chooseBestTribeWindowForSkill(targetSkill, demandBySkillTribe, settings, skillsConfig);
      trialGroup.enabled = true;
      trialGroup.campers = [];
      trialGroup.tribes = {};

      const trialGroupsBySkill = buildGroupIndex(trial.groups);
      const targetRequesters = campers
        .filter((camper) => {
          const camperKey = getCamperAssignmentKey(camper);
          if ((trial.assignments[camperKey] || {})[candidate.rotation] !== undefined) return false;
          return canJoinGroup(camper, trialGroup, trial.assignedSkillMap[camperKey], targetSkill, skillsConfig);
        })
        .sort((a, b) => preferenceRank(a, targetSkill) - preferenceRank(b, targetSkill) || a.name.localeCompare(b.name));
      targetRequesters.forEach((camper) => {
        if (trialGroup.campers.length >= trialGroup.maxSize) return;
        const camperKey = getCamperAssignmentKey(camper);
        if ((trial.assignments[camperKey] || {})[candidate.rotation] !== undefined) return;
        if (!canJoinGroup(camper, trialGroup, trial.assignedSkillMap[camperKey], targetSkill, skillsConfig)) return;
        assignCamperToGroup(camper, candidate.rotation, candidate.gKey, trial.groups, trial.assignments, trial.assignedSkillMap);
      });

      fillByPreferenceRounds({
        campers,
        groups: trial.groups,
        groupsBySkill: trialGroupsBySkill,
        assignments: trial.assignments,
        assignedSkillMap: trial.assignedSkillMap,
        skillsConfig,
        rand,
        demandBySkill,
        demandRankBySkill: buildDemandRankBySkill(demandBySkill),
        groupMin,
        lowDemandSet: dynamicLowDemandSkills(demandBySkill, groupMin),
        constrainedDemandSkills: [],
      });
      enforceHardGroupMinimums({
        campers,
        groups: trial.groups,
        assignments: trial.assignments,
        assignedSkillMap: trial.assignedSkillMap,
        skillsConfig,
        rand,
        groupMin,
        counters,
        demandBySkill,
        demandRankBySkill: buildDemandRankBySkill(demandBySkill),
      });

      counters.layoutMutationAttempts += 1;
      const trialSolution = {
        groups: trial.groups,
        assignments: trial.assignments,
      };
      const checks = validateSolutionConstraints(trialSolution, { campers, staffers, skillsConfig, settings });
      if (hasBlockingConstraintViolations(checks, { skillsConfig, staffers })) {
        counters.layoutMutationRejected += 1;
        continue;
      }
      const trialScore = computeScore(
        campers,
        trial.assignments,
        trial.groups,
        dynamicMinDelta,
        groupMin,
        demandBySkill,
        scarcityPenaltySkills,
        { weightedDemandBySkill, rankDemandBySkill, staffSkillPriority, skillsConfig }
      );
      if (compareScores(trialScore, currentScore) < 0) {
        copyAssignmentStateIntoTarget(groups, assignments, assignedSkillMap, trial);
        counters.layoutMutationAccepted += 1;
        return true;
      }
      counters.layoutMutationRejected += 1;
    }
  }
  return false;
}

function buildDemandDiagnostics(input, solution, staticContext = null) {
  const campers = input.campers || [];
  const staffers = input.staffers || [];
  const settings = input.settings || {};
  const skillsConfig = input.skillsConfig || {};
  const groupMin = settings.groupMin ?? 10;
  const tables = staticContext || computeDemandTables(campers, settings, skillsConfig);
  const demandBySkill = tables.demandBySkill || {};
  const preferenceDemandBySkill = tables.preferenceDemandBySkill || demandBySkill;
  const eligiblePreferenceDemandBySkill = tables.eligiblePreferenceDemandBySkill || {};
  const requiredDemandBySkill = tables.requiredDemandBySkill || {};
  const weightedDemandBySkill = tables.weightedDemandBySkill || {};
  const rankDemandBySkill = tables.rankDemandBySkill || {};
  const targetRunPlans = solution.targetRunPlans || {};
  const skillNames = new Set([
    ...Object.keys(skillsConfig || {}),
    ...Object.keys(demandBySkill || {}),
    ...Object.keys(targetRunPlans),
  ]);
  (solution.groups || []).forEach((rotationGroups) => {
    Object.keys(rotationGroups || {}).forEach((gKey) => {
      const group = rotationGroups[gKey];
      if (group?.skill) skillNames.add(group.skill);
    });
  });

  const skills = {};
  Array.from(skillNames).sort((a, b) => a.localeCompare(b)).forEach((skill) => {
    const rankDemand = rankDemandBySkill[skill] || { 1: 0, 2: 0, 3: 0, '4+': 0 };
    const qualifiedStaff = staffers.filter((staffer) => staffCanTeach(staffer, skill));
    let maxFeasibleRuns = 0;
    for (let rotation = 0; rotation < ROTATION_COUNT; rotation++) {
      if (!skillAllowsRotation(skill, rotation, skillsConfig)) continue;
      maxFeasibleRuns += qualifiedStaff.length;
    }

    let plannedRuns = 0;
    let plannedCapacity = 0;
    let filledSeats = 0;
    let fullRuns = 0;
    let underMinRuns = 0;
    (solution.groups || []).forEach((rotationGroups, rotation) => {
      Object.keys(rotationGroups || {}).forEach((gKey) => {
        const group = rotationGroups[gKey];
        if (!group || !isGroupEnabled(group) || group.skill !== skill) return;
        const size = (group.campers || []).length;
        const maxSize = getSkillMaxSize(skill, settings.groupMax ?? 25, skillsConfig, rotation);
        const minSize = getSkillMinSize(skill, groupMin, skillsConfig, rotation);
        plannedRuns += 1;
        plannedCapacity += Math.max(1, group.maxSize || maxSize);
        filledSeats += size;
        if (size >= Math.max(1, group.maxSize || maxSize)) fullRuns += 1;
        if (size > 0 && size < minSize) underMinRuns += 1;
      });
    });

    const weightedDemand = weightedDemandBySkill[skill] || 0;
    const weightedCapacity = plannedCapacity * getPreferenceDemandWeight(1);
    const unmetEstimatedSeats = Math.max(0, (demandBySkill[skill] || 0) - filledSeats);
    const unmetWeightedDemand = Math.max(0, weightedDemand - weightedCapacity);
    const limitingFactors = [];
    if (qualifiedStaff.length === 0 && (demandBySkill[skill] || 0) > 0) limitingFactors.push('no-qualified-staff');
    if (maxFeasibleRuns <= plannedRuns && unmetWeightedDemand > 0) limitingFactors.push('qualified-staff-capacity');
    if (plannedRuns === 0 && (demandBySkill[skill] || 0) > 0) limitingFactors.push('not-planned');
    if (underMinRuns > 0) limitingFactors.push('under-minimum-runs');

    const targetRunPlan = targetRunPlans[skill];
    const committedGroupDiagnostics = (targetRunPlan?.groups || []).map((plannedGroup) => {
      const finalGroup = solution.groups?.[plannedGroup.rotation]?.[plannedGroup.gKey];
      const finalSize = finalGroup && isGroupEnabled(finalGroup) ? (finalGroup.campers || []).length : 0;
      const survived = !!finalGroup && isGroupEnabled(finalGroup) && finalSize >= plannedGroup.minSize;
      let outcome = 'stable';
      if (!survived && plannedGroup.filledAfterBalance === 0) outcome = 'empty-after-commitment-fill';
      else if (!survived && plannedGroup.filledAfterBalance < plannedGroup.minSize) outcome = 'collapsed-below-minimum';
      else if (!survived) outcome = 'disabled-during-repair';
      return {
        ...plannedGroup,
        finalSize,
        survived,
        outcome,
      };
    });
    const survivingRuns = committedGroupDiagnostics.filter((group) => group.survived).length;
    const runsReachedMinimum = committedGroupDiagnostics.filter((group) => (
      group.filledAfterMinimum >= group.minSize
    )).length;
    const filledCommittedRuns = committedGroupDiagnostics.filter((group) => (
      group.filledAfterBalance >= group.minSize
    )).length;
    const emptyCommittedRuns = committedGroupDiagnostics.filter((group) => (
      group.filledAfterBalance === 0
    )).length;
    const targetRunAttrition = Math.max(0, (targetRunPlan?.committedRuns || 0) - survivingRuns);
    const finalTargetRunShortfall = Math.max(0, (targetRunPlan?.targetRuns || 0) - survivingRuns);
    const optionalTargetRunDiagnostics = skill === ONE_EIGHT || skillsConfig[skill]?.requiredForEligible
      ? {}
      : {
        targetRuns: targetRunPlan?.targetRuns ?? 0,
        committedRuns: targetRunPlan?.committedRuns ?? 0,
        targetRunReason: targetRunPlan?.targetRunReason || 'below-minimum-demand',
        allocationShortfall: targetRunPlan?.allocationShortfall ?? 0,
        runsReachedMinimum,
        filledCommittedRuns,
        survivingRuns,
        emptyCommittedRuns,
        targetRunAttrition,
        targetRunShortfall: finalTargetRunShortfall,
        commitmentSeatShortfall: committedGroupDiagnostics.reduce((sum, group) => (
          sum + (group.commitmentSeatShortfall || 0)
        ), 0),
        committedGroupDiagnostics,
      };
    skills[skill] = {
      requestCount: demandBySkill[skill] || 0,
      preferenceRequestCount: preferenceDemandBySkill[skill] || 0,
      requiredPlacementCount: requiredDemandBySkill[skill] || 0,
      weightedDemand,
      rankDemand: {
        1: rankDemand[1] || 0,
        2: rankDemand[2] || 0,
        3: rankDemand[3] || 0,
        '4+': rankDemand['4+'] || 0,
      },
      eligibleRequestCount: targetRunPlan?.eligibleDemand
        ?? eligiblePreferenceDemandBySkill[skill]
        ?? demandBySkill[skill]
        ?? 0,
      qualifiedStaffCount: qualifiedStaff.length,
      maxFeasibleRuns,
      plannedRuns,
      plannedCapacity,
      filledSeats,
      fullRuns,
      underMinRuns,
      unmetEstimatedSeats,
      unmetWeightedDemand,
      saturatedWithUnmetDemand: fullRuns > 0 && fullRuns === plannedRuns && unmetWeightedDemand > 0,
      limitingFactors,
      ...optionalTargetRunDiagnostics,
    };
  });

  const rotations = Array.from({ length: ROTATION_COUNT }, (_, rotation) => {
    const underfilledGroups = [];
    const saturatedHighDemandGroups = [];
    const rotationGroups = (solution.groups || [])[rotation] || {};
    Object.keys(rotationGroups).sort((a, b) => a.localeCompare(b)).forEach((gKey) => {
      const group = rotationGroups[gKey];
      if (!group || !isGroupEnabled(group)) return;
      const size = (group.campers || []).length;
      const maxSize = Math.max(1, group.maxSize || (settings.groupMax ?? 25));
      const minSize = getSkillMinSize(group.skill, groupMin, skillsConfig, rotation);
      if (size > 0 && size < minSize) {
        underfilledGroups.push({ groupKey: gKey, skill: group.skill, size, minSize });
      }
      const skillDiag = skills[group.skill] || {};
      if (size >= maxSize && skillDiag.unmetWeightedDemand > 0) {
        saturatedHighDemandGroups.push({ groupKey: gKey, skill: group.skill, size, maxSize });
      }
    });
    return { rotation, underfilledGroups, saturatedHighDemandGroups };
  });

  return {
    skills,
    rotations,
    phases: solution.phaseSnapshots || {},
  };
}

export function validateSolutionConstraints(solution, input) {
  const settings = input.settings || {};
  const groupMin = settings.groupMin ?? 10;
  const groupMax = settings.groupMax ?? 25;
  const oneEightRotation = settings.oneEightRotation ?? 4;
  const skillsConfig = input.skillsConfig || {};
  const oneEightUsesBlockSchedule = skillsConfig[ONE_EIGHT]?.requiredForEligible
    && getRequiredSchedule(skillsConfig[ONE_EIGHT]).mode === REQUIRED_SCHEDULE_MODE_PER_BLOCK;
  const requiredOneEightRotations = getSkillFixedRotations(ONE_EIGHT, skillsConfig);
  const expectedOneEightRotations = requiredOneEightRotations.length > 0 ? requiredOneEightRotations : [oneEightRotation];
  const campers = input.campers || [];
  const groups = solution.groups || [];
  const assignments = solution.assignments || {};

  const checks = {
    onePerRotationViolations: 0,
    requestedOnlyViolations: 0,
    repeatedSkillViolations: 0,
    groupMinViolations: 0,
    groupCapacityViolations: 0,
    tribeAdjacencyViolations: 0,
    eligibilityViolations: 0,
    staffPerRotationViolations: 0,
    unstaffedGroupViolations: 0,
    requiredSkillViolations: 0,
    oneEightRunViolations: 0,
  };

  const oneEightRuns = new Set();
  groups.forEach((rotationGroups, r) => {
    const staffSeen = new Set();
    Object.keys(rotationGroups || {}).forEach((gKey) => {
      const g = rotationGroups[gKey];
      if (!isGroupEnabled(g)) return;
      const skillMin = getSkillMinSize(g.skill, groupMin, skillsConfig, r);
      const skillMax = getSkillMaxSize(g.skill, groupMax, skillsConfig, r);
      if ((g.campers || []).length > 0 && !g.staff) checks.unstaffedGroupViolations += 1;
      if (g.staff) {
        if (staffSeen.has(g.staff)) checks.staffPerRotationViolations += 1;
        staffSeen.add(g.staff);
      }
      if ((g.campers || []).length > 0 && (g.campers || []).length < skillMin) checks.groupMinViolations += 1;
      if ((g.campers || []).length > skillMax) checks.groupCapacityViolations += 1;
      const tribes = Object.keys(g.tribes || {}).filter((tribe) => (g.tribes?.[tribe] || 0) > 0);
      if (!canGroupTribes(tribes, null, settings.locationConfig?.tribes || createDefaultTribes())) {
        checks.tribeAdjacencyViolations += 1;
      }
      if (g.skill === ONE_EIGHT && !oneEightUsesBlockSchedule) {
        oneEightRuns.add(r);
        if (!expectedOneEightRotations.includes(r)) checks.oneEightRunViolations += 1;
      }
    });
  });
  if (!oneEightUsesBlockSchedule) {
    expectedOneEightRotations.forEach((rotation) => {
      if (!oneEightRuns.has(rotation)) checks.oneEightRunViolations += 1;
    });
  }

  campers.forEach((camper) => {
    const camAssign = assignments[getCamperAssignmentKey(camper)] || {};
    const rotationSet = new Set();
    const skillCounts = Object.create(null);
    Object.keys(camAssign).forEach((rStr) => {
      const r = Number(rStr);
      if (rotationSet.has(r)) checks.onePerRotationViolations += 1;
      rotationSet.add(r);
      const gKey = camAssign[r];
      const group = groups[r] && groups[r][gKey];
      if (!group) return;
      if (!isGroupEnabled(group)) return;
      const requiredSkill = !!skillsConfig[group.skill]?.requiredForEligible;
      const repeatLimit = getSkillRepeatLimit(group.skill, camper, skillsConfig);
      if (!requiredSkill && !camperHasPreference(camper, group.skill)) checks.requestedOnlyViolations += 1;
      if ((skillCounts[group.skill] || 0) >= repeatLimit) checks.repeatedSkillViolations += 1;
      skillCounts[group.skill] = (skillCounts[group.skill] || 0) + 1;
      if (!isSkillAllowedForCamper(group.skill, camper, skillsConfig)) checks.eligibilityViolations += 1;
    });

    Object.keys(skillsConfig || {}).forEach((skill) => {
      const config = skillsConfig[skill];
      if (!config?.requiredForEligible || !isSkillAllowedForCamper(skill, camper, skillsConfig)) return;
      const assignedRotations = Object.keys(camAssign)
        .map(Number)
        .filter((rotation) => groups[rotation]?.[camAssign[rotation]]?.skill === skill);
      if (!requiredScheduleIsSatisfied(config, assignedRotations)) checks.requiredSkillViolations += 1;
    });
  });

  return checks;
}

function runSingleStart(input, iterationSeed, precomputed = null) {
  const startMs = Date.now();
  const timings = {
    layoutMs: 0,
    commitmentsMs: 0,
    fillMs: 0,
    swapRepairMs: 0,
    destroyRepairMs: 0,
    layoutMutationMs: 0,
    hardMinMs: 0,
    scoringMs: 0,
  };
  const rand = createRng(iterationSeed);
  const settings = input.settings || {};
  const dynamicMinDelta = settings.dynamicMinDelta ?? DEFAULT_DYNAMIC_MIN_DELTA;
  const groupMin = settings.groupMin ?? 10;

  const layoutStarted = Date.now();
  const { groups: layoutGroups, staffAssignments, demandBySkill } = buildStaffFirstLayout(input, rand, precomputed);
  timings.layoutMs += Date.now() - layoutStarted;
  const ordinaryDemandBySkill = (precomputed && precomputed.preferenceDemandBySkill) || demandBySkill;
  const demandRankBySkill = (precomputed && precomputed.demandRankBySkill) || buildDemandRankBySkill(ordinaryDemandBySkill);
  const demandBySkillTribe = (precomputed && precomputed.demandBySkillTribe)
    || computeDemandTables(input.campers || [], input.settings || {}, input.skillsConfig || {}).demandBySkillTribe;
  const weightedDemandBySkill = (precomputed && precomputed.weightedDemandBySkill)
    || computeDemandTables(input.campers || [], input.settings || {}, input.skillsConfig || {}).weightedDemandBySkill;
  const rankDemandBySkill = (precomputed && precomputed.rankDemandBySkill)
    || computeDemandTables(input.campers || [], input.settings || {}, input.skillsConfig || {}).rankDemandBySkill;
  const staffSkillPriority = (precomputed && precomputed.staffSkillPriority) || buildStaffSkillPriority(input.staffers || []);
  const groups = cloneGroupLayout(layoutGroups);
  let groupsBySkill = buildGroupIndex(groups);
  const campers = shuffleInPlace((input.campers || []).slice(), rand);
  const { assignments, assignedSkillMap } = initializeAssignmentState(campers);

  const counters = {
    insertionSuccess: 0,
    swapAttempts: 0,
    swapSuccess: 0,
    destroyRepairAttempts: 0,
    destroyRepairSuccess: 0,
    hardMinGroupsDisabled: 0,
    hardMinReassignments: 0,
    hardMinDrops: 0,
    hardMinGroupsRescued: 0,
    hardMinRescuePlacements: 0,
    hardMinRescueRebalances: 0,
    commitmentPlacements: 0,
    commitmentShortfalls: 0,
    constrainedCommitments: 0,
    targetRunCommitments: 0,
    targetRunGroupsCreated: 0,
    targetRunGroupsConverted: 0,
    targetRunCommitmentShortfalls: 0,
    targetMinimumPlacements: 0,
    targetBalancePlacements: 0,
    invalidRepeatAssignmentsRemoved: 0,
    requiredGroupsCreated: 0,
    requiredPlacements: 0,
    finalSwapPasses: 0,
    finalSwapAttempts: 0,
    finalSwapResolved: 0,
    layoutMutationAttempts: 0,
    layoutMutationAccepted: 0,
    layoutMutationRejected: 0,
  };

  ensureRequiredSchedules({
    campers,
    staffers: input.staffers || [],
    groups,
    assignments,
    assignedSkillMap,
    skillsConfig: input.skillsConfig || {},
    settings,
    counters,
  });
  groupsBySkill = buildGroupIndex(groups);
  const phaseSnapshots = {
    afterRequired: summarizeAssignmentPhase(campers, assignments),
  };

  const commitmentsStarted = Date.now();
  const demandTables = precomputed || computeDemandTables(input.campers || [], input.settings || {}, input.skillsConfig || {});
  const targetRunPlans = buildProgramShapeTargetRunPlans(
    input,
    groups,
    assignments,
    assignedSkillMap,
    demandTables,
    precomputed?.teachersPerSkill || {}
  );
  const targetRunCommitments = ensureProgramShapeTargetRunGroups(input, groups, targetRunPlans, counters);
  counters.targetRunCommitments = targetRunCommitments.length;
  groupsBySkill = buildGroupIndex(groups);
  const commitments = buildConstrainedDemandCommitments(
    groups,
    ordinaryDemandBySkill,
    demandBySkillTribe,
    weightedDemandBySkill,
    rankDemandBySkill,
    groupMin,
    settings.groupMax ?? 25,
    precomputed && precomputed.constrainedDemandSkills
  );
  counters.constrainedCommitments = commitments.length;
  const commitmentsByGroup = new Map();
  [...targetRunCommitments, ...commitments].forEach((commitment) => {
    const key = `${commitment.rotation}|${commitment.gKey}`;
    const effectiveMin = getSkillMinSize(
      commitment.skill,
      groupMin,
      input.skillsConfig || {},
      commitment.rotation
    );
    const normalized = {
      ...commitment,
      minSize: commitment.minSize || effectiveMin,
      targetSize: Math.max(commitment.targetSize || effectiveMin, effectiveMin),
    };
    const existing = commitmentsByGroup.get(key);
    if (!existing || normalized.targetSize > existing.targetSize) {
      commitmentsByGroup.set(key, existing ? { ...existing, ...normalized } : normalized);
    }
  });
  const allCommitments = Array.from(commitmentsByGroup.values()).sort((a, b) => (
    (a.targetRound || 0) - (b.targetRound || 0)
    || (a.planOrder || 0) - (b.planOrder || 0)
    || a.rotation - b.rotation
    || a.gKey.localeCompare(b.gKey)
  ));
  const protectedGroupKeys = new Set(allCommitments.map((c) => `${c.rotation}|${c.gKey}`));
  const targetFill = fillCommittedTargetGroups({
    campers,
    groups,
    groupsBySkill,
    assignments,
    assignedSkillMap,
    skillsConfig: input.skillsConfig || {},
    rand,
    counters,
    groupMin,
  }, allCommitments);
  phaseSnapshots.afterTargetMinimumSeeding = targetFill.afterMinimum;
  phaseSnapshots.afterTargetBalancing = targetFill.afterBalance;
  timings.commitmentsMs += Date.now() - commitmentsStarted;

  const fillStarted = Date.now();
  fillByPreferenceRounds({
    campers,
    groups,
    groupsBySkill,
    assignments,
    assignedSkillMap,
    skillsConfig: input.skillsConfig || {},
    rand,
    demandBySkill: ordinaryDemandBySkill,
    demandRankBySkill,
    groupMin,
    lowDemandSet: precomputed && precomputed.lowDemandSet,
    constrainedDemandSkills: precomputed && precomputed.constrainedDemandSkills,
  });
  phaseSnapshots.afterPreferenceFill = summarizeAssignmentPhase(campers, assignments);
  timings.fillMs += Date.now() - fillStarted;

  // Repair loop
  let changed = true;
  let guard = 0;
  const swapStarted = Date.now();
  while (changed && guard < 6) {
    changed = attemptSwapRepairs({
      campers,
      groups,
      groupsBySkill,
      assignments,
      assignedSkillMap,
      skillsConfig: input.skillsConfig || {},
      rand,
      counters,
      demandBySkill: ordinaryDemandBySkill,
      demandRankBySkill,
      groupMin,
      protectedGroupKeys,
    });
    guard += 1;
  }
  phaseSnapshots.afterSwapRepair = summarizeAssignmentPhase(campers, assignments);
  timings.swapRepairMs += Date.now() - swapStarted;

  // Destroy & repair neighborhoods.
  let neighborhoodSteps = 0;
  const destroyStarted = Date.now();
  while (neighborhoodSteps < 4) {
    const improved = runDestroyRepair({
      campers,
      groups,
      groupsBySkill,
      assignments,
      assignedSkillMap,
      skillsConfig: input.skillsConfig || {},
      rand,
      counters,
      demandBySkill: ordinaryDemandBySkill,
      demandRankBySkill,
      groupMin,
      protectedGroupKeys,
    });
    if (!improved) break;
    neighborhoodSteps += 1;
  }
  phaseSnapshots.afterDestroyRepair = summarizeAssignmentPhase(campers, assignments);
  timings.destroyRepairMs += Date.now() - destroyStarted;

  // Layout mutation is intentionally out of the active path. The revamp now
  // spends that budget on deterministic placement order and hard-min rescue.
  timings.layoutMutationMs += 0;

  const hardMinStarted = Date.now();
  enforceHardGroupMinimums({
    campers,
    groups,
    assignments,
    assignedSkillMap,
    skillsConfig: input.skillsConfig || {},
    rand,
    groupMin,
    counters,
    demandBySkill,
    demandRankBySkill,
  });
  phaseSnapshots.afterHardMinCleanup = summarizeAssignmentPhase(campers, assignments);
  timings.hardMinMs += Date.now() - hardMinStarted;
  ensureRequiredSchedules({
    campers,
    staffers: input.staffers || [],
    groups,
    assignments,
    assignedSkillMap,
    skillsConfig: input.skillsConfig || {},
    settings,
    counters,
  });
  const invalidRepeatsRemoved = removeInvalidRepeatedAssignments(
    campers,
    groups,
    assignments,
    assignedSkillMap,
    input.skillsConfig || {},
    counters
  );
  if (invalidRepeatsRemoved > 0) {
    groupsBySkill = buildGroupIndex(groups);
    fillByPreferenceRounds({
      campers,
      groups,
      groupsBySkill,
      assignments,
      assignedSkillMap,
      skillsConfig: input.skillsConfig || {},
      rand,
      demandBySkill: ordinaryDemandBySkill,
      demandRankBySkill,
      groupMin,
      lowDemandSet: precomputed && precomputed.lowDemandSet,
      constrainedDemandSkills: precomputed && precomputed.constrainedDemandSkills,
    });
    enforceHardGroupMinimums({
      campers,
      groups,
      assignments,
      assignedSkillMap,
      skillsConfig: input.skillsConfig || {},
      rand,
      groupMin,
      counters,
      demandBySkill,
      demandRankBySkill,
    });
    ensureRequiredSchedules({
      campers,
      staffers: input.staffers || [],
      groups,
      assignments,
      assignedSkillMap,
      skillsConfig: input.skillsConfig || {},
      settings,
      counters,
    });
  }
  groupsBySkill = buildGroupIndex(groups);
  phaseSnapshots.final = summarizeAssignmentPhase(campers, assignments);

  const scoringStarted = Date.now();
  const unplaced = gatherUnplacedCampers(campers, assignments);
  const preferenceHits = computePreferenceHits(campers, assignments, groups);
  const score = computeScore(
    campers,
    assignments,
    groups,
    dynamicMinDelta,
    groupMin,
    demandBySkill,
    precomputed && precomputed.scarcityPenaltySkills,
    {
      weightedDemandBySkill: precomputed && precomputed.weightedDemandBySkill,
      rankDemandBySkill: precomputed && precomputed.rankDemandBySkill,
      staffSkillPriority,
      skillsConfig: input.skillsConfig || {},
    }
  );
  timings.scoringMs += Date.now() - scoringStarted;
  timings.totalStartMs = Date.now() - startMs;
  const solution = {
    assignments,
    groups,
    unplaced,
    preferenceHits,
    staffAssignments,
    score,
    targetRunPlans: Object.fromEntries(targetRunPlans.map((plan) => [plan.skill, {
      eligibleDemand: plan.eligibleDemand,
      targetRuns: plan.targetRuns,
      committedRuns: plan.committedRuns,
      targetRunReason: plan.targetRunReason,
      targetRunShortfall: plan.targetRunShortfall,
      allocationShortfall: plan.allocationShortfall || 0,
      legalRotationCapacity: plan.legalRotationCapacity,
      staffQualifiedRotationCapacity: plan.staffQualifiedRotationCapacity,
      effectiveMin: plan.effectiveMin,
      effectiveMax: plan.effectiveMax,
      groups: targetFill.groupStats.filter((group) => group.skill === plan.skill),
    }])),
    phaseSnapshots,
  };
  return { solution, counters, timings };
}

export function optimizeSchedule(input, options = {}) {
  const startedAt = Date.now();
  const settings = input.settings || {};
  const maxRuntimeMs = options.maxRuntimeMs ?? 120000;
  const maxStarts = Number.isFinite(options.maxStarts) ? options.maxStarts : Number.POSITIVE_INFINITY;
  const skipConstraintValidation = options.skipConstraintValidation === true;
  const baseSeed = toIntSeed(options.seed ?? buildDeterministicSeed(input));
  const startIteration = options.startIteration ?? 0;
  const onProgress = options.onProgress;
  const progressIntervalMs = options.progressIntervalMs ?? 250;
  const staticContext = options.staticContext || buildStaticOptimizerContext(input);

  let iterations = 0;
  let best = null;
  let bestScore = null;
  let lastProgressAt = startedAt;
  const aggregateCounters = {
    insertionSuccess: 0,
    swapAttempts: 0,
    swapSuccess: 0,
    destroyRepairAttempts: 0,
    destroyRepairSuccess: 0,
    hardMinGroupsDisabled: 0,
    hardMinReassignments: 0,
    hardMinDrops: 0,
    hardMinGroupsRescued: 0,
    hardMinRescuePlacements: 0,
    hardMinRescueRebalances: 0,
    commitmentPlacements: 0,
    commitmentShortfalls: 0,
    constrainedCommitments: 0,
    targetRunCommitments: 0,
    targetRunGroupsCreated: 0,
    targetRunGroupsConverted: 0,
    targetRunCommitmentShortfalls: 0,
    targetMinimumPlacements: 0,
    targetBalancePlacements: 0,
    invalidRepeatAssignmentsRemoved: 0,
    requiredGroupsCreated: 0,
    requiredPlacements: 0,
    finalSwapPasses: 0,
    finalSwapAttempts: 0,
    finalSwapResolved: 0,
    layoutMutationAttempts: 0,
    layoutMutationAccepted: 0,
    layoutMutationRejected: 0,
  };
  const aggregateTimings = {
    layoutMs: 0,
    commitmentsMs: 0,
    fillMs: 0,
    swapRepairMs: 0,
    destroyRepairMs: 0,
    layoutMutationMs: 0,
    hardMinMs: 0,
    scoringMs: 0,
    totalStartMs: 0,
  };

  while (iterations < maxStarts && (Date.now() - startedAt) < maxRuntimeMs) {
    const iterIndex = startIteration + iterations;
    const iterSeed = (baseSeed + Math.imul(iterIndex + 1, 2654435761)) >>> 0;
    const { solution, counters, timings } = runSingleStart(input, iterSeed || 1, staticContext);

    aggregateCounters.insertionSuccess += counters.insertionSuccess;
    aggregateCounters.swapAttempts += counters.swapAttempts;
    aggregateCounters.swapSuccess += counters.swapSuccess;
    aggregateCounters.destroyRepairAttempts += counters.destroyRepairAttempts;
    aggregateCounters.destroyRepairSuccess += counters.destroyRepairSuccess;
    aggregateCounters.hardMinGroupsDisabled += counters.hardMinGroupsDisabled;
    aggregateCounters.hardMinReassignments += counters.hardMinReassignments;
    aggregateCounters.hardMinDrops += counters.hardMinDrops;
    aggregateCounters.hardMinGroupsRescued += counters.hardMinGroupsRescued;
    aggregateCounters.hardMinRescuePlacements += counters.hardMinRescuePlacements;
    aggregateCounters.hardMinRescueRebalances += counters.hardMinRescueRebalances;
    aggregateCounters.commitmentPlacements += counters.commitmentPlacements;
    aggregateCounters.commitmentShortfalls += counters.commitmentShortfalls;
    aggregateCounters.constrainedCommitments += counters.constrainedCommitments;
    aggregateCounters.targetRunCommitments += counters.targetRunCommitments;
    aggregateCounters.targetRunGroupsCreated += counters.targetRunGroupsCreated;
    aggregateCounters.targetRunGroupsConverted += counters.targetRunGroupsConverted;
    aggregateCounters.targetRunCommitmentShortfalls += counters.targetRunCommitmentShortfalls;
    aggregateCounters.targetMinimumPlacements += counters.targetMinimumPlacements;
    aggregateCounters.targetBalancePlacements += counters.targetBalancePlacements;
    aggregateCounters.invalidRepeatAssignmentsRemoved += counters.invalidRepeatAssignmentsRemoved;
    aggregateCounters.requiredGroupsCreated += counters.requiredGroupsCreated;
    aggregateCounters.requiredPlacements += counters.requiredPlacements;
    aggregateCounters.finalSwapPasses += counters.finalSwapPasses;
    aggregateCounters.finalSwapAttempts += counters.finalSwapAttempts;
    aggregateCounters.finalSwapResolved += counters.finalSwapResolved;
    aggregateCounters.layoutMutationAttempts += counters.layoutMutationAttempts;
    aggregateCounters.layoutMutationAccepted += counters.layoutMutationAccepted;
    aggregateCounters.layoutMutationRejected += counters.layoutMutationRejected;
    mergeStageTimings(aggregateTimings, timings);

    if (!best || compareScores(solution.score, bestScore) < 0) {
      best = solution;
      bestScore = solution.score;
    }

    iterations += 1;

    const now = Date.now();
    if (onProgress && (now - lastProgressAt) >= progressIntervalMs) {
      onProgress({
        iteration: startIteration + iterations,
        elapsedMs: now - startedAt,
        bestUnplaced: bestScore ? bestScore.unplacedCampers : null,
        bestScore,
      });
      lastProgressAt = now;
    }
    if (bestScore && bestScore.unplacedCampers === 0) break;
  }

  if (!best) {
    const fallback = runSingleStart(input, baseSeed, staticContext);
    best = fallback.solution;
    bestScore = fallback.solution.score;
    iterations = Math.max(iterations, 1);
    aggregateCounters.insertionSuccess += fallback.counters.insertionSuccess;
    aggregateCounters.swapAttempts += fallback.counters.swapAttempts;
    aggregateCounters.swapSuccess += fallback.counters.swapSuccess;
    aggregateCounters.destroyRepairAttempts += fallback.counters.destroyRepairAttempts;
    aggregateCounters.destroyRepairSuccess += fallback.counters.destroyRepairSuccess;
    aggregateCounters.hardMinGroupsDisabled += fallback.counters.hardMinGroupsDisabled;
    aggregateCounters.hardMinReassignments += fallback.counters.hardMinReassignments;
    aggregateCounters.hardMinDrops += fallback.counters.hardMinDrops;
    aggregateCounters.hardMinGroupsRescued += fallback.counters.hardMinGroupsRescued;
    aggregateCounters.hardMinRescuePlacements += fallback.counters.hardMinRescuePlacements;
    aggregateCounters.hardMinRescueRebalances += fallback.counters.hardMinRescueRebalances;
    aggregateCounters.commitmentPlacements += fallback.counters.commitmentPlacements;
    aggregateCounters.commitmentShortfalls += fallback.counters.commitmentShortfalls;
    aggregateCounters.constrainedCommitments += fallback.counters.constrainedCommitments;
    aggregateCounters.targetRunCommitments += fallback.counters.targetRunCommitments;
    aggregateCounters.targetRunGroupsCreated += fallback.counters.targetRunGroupsCreated;
    aggregateCounters.targetRunGroupsConverted += fallback.counters.targetRunGroupsConverted;
    aggregateCounters.targetRunCommitmentShortfalls += fallback.counters.targetRunCommitmentShortfalls;
    aggregateCounters.targetMinimumPlacements += fallback.counters.targetMinimumPlacements;
    aggregateCounters.targetBalancePlacements += fallback.counters.targetBalancePlacements;
    aggregateCounters.invalidRepeatAssignmentsRemoved += fallback.counters.invalidRepeatAssignmentsRemoved;
    aggregateCounters.requiredGroupsCreated += fallback.counters.requiredGroupsCreated;
    aggregateCounters.requiredPlacements += fallback.counters.requiredPlacements;
    aggregateCounters.finalSwapPasses += fallback.counters.finalSwapPasses;
    aggregateCounters.finalSwapAttempts += fallback.counters.finalSwapAttempts;
    aggregateCounters.finalSwapResolved += fallback.counters.finalSwapResolved;
    aggregateCounters.layoutMutationAttempts += fallback.counters.layoutMutationAttempts;
    aggregateCounters.layoutMutationAccepted += fallback.counters.layoutMutationAccepted;
    aggregateCounters.layoutMutationRejected += fallback.counters.layoutMutationRejected;
    mergeStageTimings(aggregateTimings, fallback.timings);
  }

  if (best && (bestScore?.unplacedCampers || 0) > 0) {
    const finalSwapSeed = (baseSeed + Math.imul(startIteration + iterations + 1, 2246822519)) >>> 0;
    const endgame = runFinalSwapOnSolution(input, best, {
      seed: finalSwapSeed || 1,
      maxDepth: 3,
      maxPasses: 3,
      maxHolesPerPass: 220,
      maxAttemptsPerPass: 2200,
      precomputed: staticContext,
    });
    mergeStageCounters(aggregateCounters, endgame.stageCounters);
    if (compareScores(endgame.solution.score, bestScore) < 0) {
      best = endgame.solution;
      bestScore = endgame.solution.score;
    }
  }

  const elapsedMs = Date.now() - startedAt;
  const constraintChecks = skipConstraintValidation
    ? {}
    : validateSolutionConstraints(best, input);
  const demandDiagnostics = buildDemandDiagnostics(input, best, staticContext);
  best = {
    ...best,
    demandDiagnostics,
  };

  return {
    solution: best,
    diagnostics: {
      seed: baseSeed,
      iterations,
      elapsedMs,
      bestScore: bestScore || best.score,
      stageCounters: aggregateCounters,
      stageTimingsMs: aggregateTimings,
      constraintChecks,
      demandDiagnostics,
      settingsSnapshot: {
        groupMin: settings.groupMin ?? 10,
        groupMax: settings.groupMax ?? 25,
        oneEightRotation: settings.oneEightRotation ?? 4,
        dynamicMinDelta: settings.dynamicMinDelta ?? DEFAULT_DYNAMIC_MIN_DELTA,
      },
    },
  };
}
